import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { BUILTIN_SCRIPTS, BUILTIN_SCRIPT_IDS } from "@/data/scripts";
import { FABLED } from "@/data/fabled";
import { LORICS } from "@/data/lorics";
import { MAX_NIGHT_STEP_NOTES, MAX_ST_NOTES, StorytellerGamePersistedSchema, StorytellerStateSchema } from "./schemas";
import { buildRegistry, type RoleRegistry } from "@/data/roleRegistry";
import { dealtIdentity, isInitialRevealComplete, needsShownIdentity } from "./identity";
import { manualEffectId } from "./effects";
import { cloneOwned, type MutationContext, sameSnapshot } from "./history";
import {
  applyEffectPlan,
  effectApplicationMoment,
  planEffectExpiry,
  planEffectTransaction,
  type EffectParticipantBinding,
  type EffectRefusal,
  type EffectTransaction,
} from "./effectResolution";
import { GAME_SCHEMA_VERSION } from "./schemas";
import {
  applyRolePlan,
  changeRoleIntent,
  correctRoleIntent,
  defaultRoleIds,
  planRoleTransaction,
  setPerceptionIntent,
  type RoleRefusal,
  type RoleTransaction,
  type TravelerArrivalPolicy,
} from "./roleResolution";
import {
  applyAlignmentPlan,
  changeAlignmentIntent,
  defaultAlignmentIds,
  planAlignmentTransaction,
  type AlignmentRefusal,
  type AlignmentTransaction,
} from "./alignmentResolution";
import {
  applyReminderPlan,
  newReminderId,
  planReminderTransaction,
  unknownOwnKey,
  type ReminderRefusal,
  type ReminderTransaction,
} from "./reminderResolution";
import { newParticipantId, participantIdAppearsIn } from "./participants";
import { participantRoleStepEntries, planNightStepStatus } from "./nightProgress";
import { planAbilityResolution, type AbilityRefusal, type AbilityResolutionRequest } from "./abilityResolution";
import {
  applyGameRuleFactPlan,
  planGameRuleFactExpiry,
  planGameRuleFactTransaction,
  type GameRuleFactRefusal,
  type GameRuleFactTransaction,
} from "./gameRuleFacts";
import { CANONICAL_ABILITY_SEMANTICS, type AbilitySemanticsRegistry } from "@/abilities/semantics";
import { detectLegacyGameVersion, migrateGameEntry } from "./gameMigration";
import { freshLifeEventWindow, pruneLifeEventWindow } from "./lifeEvents";
import {
  applyLifePlan,
  canonicalizeStartingLife,
  planLifeTransaction,
  type LifeEventSpec,
  type LifeConfirmationToken,
  type LifeIntent,
  type LifeRefusal,
  type LifeStatusTarget,
  type LifeTransaction,
} from "./lifeResolution";
import {
  applyInformationDeliveryPlan,
  planInformationDelivery,
} from "./informationDelivery";
import { initialRevealReadiness } from "@/features/setup/revealReadiness";
import { invalidatePrivatePacket } from "./privatePackets";
import { withRevealTokens } from "./revealTokens";
import { usePrivacyStore } from "./privacyStore";
import { useShellStore } from "./shellStore";
import { analyzeSetup } from "@/features/setup/setupAnalyzer";
import { isPostDeal, selectSetupContext } from "@/features/setup/setupContext";
import { assignedBagIsCoherent, canRefineSetup, matchBagToAssignments } from "@/features/setup/setupRefinement";
import { isBagType } from "@/features/setup/setupPolicies";
import { arrivalsAreTravelers, newTravelerArrival, publicTravelerRole, travelerDemonInformation, travelerNeedsArrivalCheck } from "./travelers";
import { getTraveler } from "@/data/travelers";
import { MAX_PLAYERS, MAX_TOTAL_PLAYERS, MIN_PLAYERS } from "@/data/setupCounts";
import { validateCheckpointEnvelope } from "@/firebase/checkpoint";
import { validateFirebaseWritableValue } from "@/firebase/firebaseWriteCompatibility";
import { storytellerPathSegments } from "@/firebase/paths";
import { MAX_ROOM_CODE_SHAPE } from "@/firebase/roomCode";
import type { SetupCommandResult } from "@/features/setup/setupReadiness";
import type {
  Alignment,
  BehaviorMode,
  EffectId,
  EffectInput,
  ExecutionOutcome,
  ExileOutcome,
  GameResult,
  GrimoireMode,
  GuardStamp,
  InformationActionId,
  InformationDeliveryId,
  InformationValue,
  LifeEventId,
  NightStepRecord,
  NightStepStatus,
  ParticipantId,
  PlayerId,
  ReminderId,
  ReminderInput,
  RoleId,
  Script,
  ShownAlignment,
  STPlayerRecord,
  StorytellerLobbyRecord,
  SyncMeta,
  TokenPosition,
} from "./types";

const UNDO_LIMIT = 20;

/**
 * ASTRA-10G-001: a page-local game-lifecycle token, advanced whenever the
 * current game is replaced or ends (newGame, endGame, finishGame). An
 * asynchronous workflow started for one game (Go Live) captures it and
 * revalidates before adopting its result, so a continuation that outlives
 * its game -- finished, discarded or replaced meanwhile -- never acts on the
 * game now open. Not persisted: a reload already cancels every in-flight
 * workflow.
 */
let gameLifecycle = 0;
export const gameLifecycleToken = (): number => gameLifecycle;

/** The persisted store's current schema version — also the single source
 * `merge` (Phase 9C.2B.2) passes to migrateStoreState when Zustand's own
 * persist middleware skips calling `migrate` outright, which it does
 * whenever the persisted version already equals this one. */
const STORE_VERSION = 26;

let _migrationResetFlag = false;
/** Returns true (once) when migrate() discarded incompatible persisted state. */
export function takeMigrationResetFlag(): boolean {
  const v = _migrationResetFlag;
  _migrationResetFlag = false;
  return v;
}

const newId = (): PlayerId =>
  globalThis.crypto?.randomUUID?.() ?? `p-${Math.random().toString(36).slice(2, 10)}`;

const blankPlayer = (id: PlayerId, name: string, seat: number, isEmpty = false): STPlayerRecord => ({
  id,
  name,
  seat,
  joinedAt: Date.now(),
  actualRole: "",
  shownRole: null,
  shownAlignment: null,
  behaviorMode: "normal",
  publicDisplayRole: null,
  alive: true,
  ghostVote: true,
  abilityUsed: false,
  statuses: {},
  reminders: [],
  effects: [],
  stNotes: "",
  isTraveler: false,
  isEmpty,
});

// Used only when creating an arrival or filling an empty seat. A planned
// ordinary identity must not become a late arrival's identity.
//
// FINAL POPULATION CLOSURE, Section 5: whether this arrival/fill must be a
// Traveler is decided here, atomically, against the game state as it stood
// before this seat was touched -- never re-derived afterward from
// occupancy counts the fill itself just changed (that reintroduced the
// exact timing bug this section closes: filling a seat could silently push
// occupied-ordinary to the cap and then have a *later* recheck of
// arrivalsAreTravelers() spuriously suppress an explicit Traveler request).
// plannedTravelerSeat -- a planned Traveler reservation on an empty seat --
// is an equally authoritative trigger alongside the arrival-cap policy;
// blankPlayer() below never carries the marker forward, so occupying a
// reserved seat always fulfills and clears it in the same step.
const arrivalPlayer = (player: STPlayerRecord, game: StorytellerLobbyRecord): STPlayerRecord =>
  (arrivalsAreTravelers(game) || player.plannedTravelerSeat) && !player.isTraveler
    ? { ...blankPlayer(player.id, player.name, player.seat, player.isEmpty), isTraveler: true, travelerArrival: newTravelerArrival() }
    : player;

/**
 * Phase 9R.2: the single occupancy-entry point -- a real person entering
 * an empty (or brand-new) seat. Every path that seats someone
 * (addPlayer, addPlayerToSeat, assignPendingToSeat) goes through here, so
 * each one always gets a FRESH ParticipantId: the seat/PlayerId may be
 * reused, a participation identity never is. Never used for an empty
 * planned seat (those carry no participant identity at all). The fresh id
 * is applied AFTER arrivalPlayer(), which may rebuild the record from
 * blankPlayer() when designating a Traveler arrival.
 *
 * Phase 9R.2 (Astra R1): `participantId` is supplied only when the identity
 * of this exact participation instance was already fixed elsewhere -- minted
 * by the membership seating command so the server's rosterParticipants
 * record names the same instance (assignPendingToSeat), or read back from
 * that authoritative record during recovery (restoreSeatedMember). Both
 * callers validate it first; everyone else gets a freshly minted one.
 *
 * Phase 10B (SOL-10B-R1): a new participation instance always starts with
 * NO Effects -- Effects belong to a participation instance, never to a seat,
 * so nothing an empty seat object carries (even malformed or crafted state)
 * is ever inherited by the person who occupies it.
 *
 * Phase 10C: likewise NO Reminders -- a Reminder belongs to exactly one
 * participation instance, so no notation ever survives a participant
 * boundary into the next occupant (addPlayer, addPlayerToSeat,
 * assignPendingToSeat and restoreSeatedMember all come through here).
 *
 * Phase 10E: likewise Actual Alignment always starts UNRESOLVED -- alignment
 * belongs to a participation instance, never to a seat, so a stale or
 * malformed `actualAlignment` left on the reusable empty seat object is never
 * inherited (normal seating, planned-seat fill, pending-player seating and
 * recovery seating all come through here). Legacy empty-seat state carrying
 * one stays loadable; only this boundary drops it.
 */
const occupySeat = (
  seat: STPlayerRecord,
  name: string,
  game: StorytellerLobbyRecord,
  participantId: ParticipantId = newParticipantId(),
): STPlayerRecord => {
  const { actualAlignment: _staleSeatAlignment, ...occupant } = arrivalPlayer({ ...seat, name, isEmpty: false }, game);
  return { ...occupant, participantId, effects: [], reminders: [] };
};

const clone = <T,>(v: T): T =>
  typeof structuredClone === "function"
    ? structuredClone(v)
    : JSON.parse(JSON.stringify(v));

/**
 * FINAL SEAT & TRAVELLER RESERVATION CLOSURE, Section 2: every command that
 * creates new starting capacity before Reveal must refuse atomically -- no
 * seat, no player, no plan increment -- once EITHER the plan or physical
 * seats are already at the supported cap. Checking only plannedPlayerCount
 * left a bypass whenever the two had already diverged (e.g. legacy/
 * adversarial state, or a plan reduced independently of physical seats);
 * checking both closes it regardless of how they diverged.
 *
 * Phase 9R.3 (B7): that check used to apply ONLY before Reveal, so the
 * total cap disappeared the moment Reveal committed. Physical-seat capacity
 * is now enforced in every lifecycle state; the plan remains an additional
 * guard only before Reveal, because afterward it is a frozen historical
 * record -- a revealed plan of 20 whose live table later dropped to 19 must
 * still accept one legitimate replacement back to 20.
 */
const atCapacity = (game: StorytellerLobbyRecord): boolean =>
  game.seatOrder.length >= MAX_TOTAL_PLAYERS ||
  (!isInitialRevealComplete(game) && game.plannedPlayerCount >= MAX_TOTAL_PLAYERS);

/**
 * Phase 9R.3 (B7): occupying an EXISTING empty seat can never admit live
 * participant 21. Under the physical-capacity invariant above an empty seat
 * implies fewer than 20 occupants, so this only ever refuses malformed/
 * legacy/adversarial state carrying extra physical seats. Counted with the
 * Setup population model -- never a second definition of player count.
 */
const atOccupancyCapacity = (game: StorytellerLobbyRecord): boolean => {
  const { occupiedNonTravelerCount, occupiedTravelerCount } = selectSetupContext(game).population;
  return occupiedNonTravelerCount + occupiedTravelerCount >= MAX_TOTAL_PLAYERS;
};

export type AddScriptResult = { ok: true } | { ok: false; error: string };

/** Phase 9D.3: result of the Authoritative Information command. Mirrors
 * the codebase's existing {ok:true}|{ok:false,message} command-result
 * shape (e.g. SetupCommandResult) rather than a bare id/null, since
 * structural validation has several distinct rejection reasons a caller
 * should be able to surface. */
export type RecordInformationDeliveryResult =
  | { ok: true; id: InformationDeliveryId }
  | { ok: false; message: string };

/** Phase 10A: result of every Life command. `changed: false` is a true
 * no-op (nothing committed); a refusal changes nothing either. A
 * `needsConfirmation` refusal names the exceptional case the caller may
 * explicitly confirm and resubmit (see ExecutionIntent). */
export type LifeCommandResult =
  | { ok: true; changed: boolean; eventIds: LifeEventId[] }
  | LifeRefusal;

/** Phase 10B: result of every Effect command. `changed: false` is a true
 * no-op (nothing committed); a refusal changes nothing either. `effectIds`
 * are the Effects the transaction created, in intent order. */
export type EffectCommandResult =
  | { ok: true; changed: boolean; effectIds: EffectId[] }
  | EffectRefusal;

/** Phase 10C: result of every Reminder command. `changed: false` is a true
 * no-op (nothing committed); a refusal changes nothing either.
 * `reminderIds` are the Reminders the transaction placed, in intent order. */
export type ReminderCommandResult =
  | { ok: true; changed: boolean; reminderIds: ReminderId[] }
  | ReminderRefusal;

/** Phase 10D: result of every Role/perception command. `changed: false` is a
 * true no-op (nothing committed, no Undo, no localSeq step); a refusal changes
 * nothing either. */
export type RoleCommandResult =
  | { ok: true; changed: boolean }
  | RoleRefusal;

/** Phase 10E: result of every Actual Alignment command. `changed: false` is a
 * true no-op (nothing committed, no Undo, no localSeq step, no packet epoch);
 * a refusal changes nothing either. */
export type AlignmentCommandResult =
  | { ok: true; changed: boolean }
  | AlignmentRefusal;

/** Phase 10F: result of resolveAbility. `changed: false` is a true no-op
 * (nothing committed, no Undo, no localSeq step); every refusal changes
 * nothing. */
export type AbilityCommandResult =
  | { ok: true; changed: false }
  | { ok: true; changed: true; resolutionId: string }
  | AbilityRefusal;

/** Phase 10G: result of resolveGameRuleFacts. `changed: false` is a true
 * no-op (nothing committed, no Undo, no localSeq step); every refusal changes
 * nothing. */
export type GameRuleFactCommandResult =
  | { ok: true; changed: boolean }
  | GameRuleFactRefusal;

export type LobbyConnection = {
  code: string;
  uid: string;
  sessionId?: string;
  status: "live" | "reconnecting";
};

export type NewGameOpts = {
  plannedPlayerCount?: number;
  /** Intended Traveler count out of plannedPlayerCount. Never derives the
   * bag from total participants -- see setupContext's targetNonTravelerCount. */
  plannedTravelerCount?: number;
  plannedRoles?: RoleId[];
  plannedFabled?: RoleId[];
  plannedLorics?: RoleId[];
};

/**
 * Phase 10H (contract §14.2): the Storyteller's explicit terminal intent. The
 * normal intents declare Good or Evil victory; End Without Result is the
 * exceptional one. Never inferred -- there is no win-condition evaluation.
 */
export type TerminalIntent =
  | { kind: "declare"; winner: Alignment }
  | { kind: "noResult" };

/**
 * Phase 10H (contract §15): page-runtime state of an in-flight terminal close
 * -- never persisted, never game state. While `closing`, gameplay mutation is
 * locked at the store's central commit seam (a game/Undo change is dropped);
 * a failed close moves to `failed`, which restores mutation (the game stays
 * live and the same intent can be retried).
 */
export type TerminalCloseState = {
  intent: TerminalIntent;
  status: "closing" | "failed";
  message: string | null;
};

/** Phase 10H (ASTRA-10H-004): what the terminal flow knows about the remote
 * outcome, as an explicit tri-state -- never inferred from truthiness:
 *  - `fresh`: this close itself just committed (or there is no lobby), so the
 *    Storyteller's own intent is the result;
 *  - `endedWithResult`: the session was ALREADY ended by an earlier attempt
 *    whose response was lost, and that commit's declared result was read
 *    back -- it overrides any new retry intent;
 *  - `endedWithoutResult`: the session was already ended and it is CONFIRMED
 *    that no result was declared (End Without Result) -- this also overrides
 *    any new retry intent. */
export type TerminalRecovery =
  | { kind: "fresh" }
  | { kind: "endedWithResult"; result: GameResult }
  | { kind: "endedWithoutResult" };

export type StorytellerStore = {
  game: StorytellerLobbyRecord | null;
  /** Phase 10H: an in-flight / failed terminal close (runtime only). */
  terminalClose: TerminalCloseState | null;
  view: "home" | "game" | "newgame";
  undoStack: StorytellerLobbyRecord[];
  selectedPlayerId: PlayerId | null;
  customScripts: Record<string, Script>;
  lobby: LobbyConnection | null;
  // ephemeral — knocks the ST has not yet processed
  pendingKnocks: { uid: string; name: string }[];
  // grimoire layout (localStorage-only, never Firebase-synced)
  grimoireMode: GrimoireMode;
  tokenPositions: Record<PlayerId, TokenPosition>;

  // --- Phase 9C.2A (OPUS-001) reconnect/acknowledgement watermarks -------
  /** Local game-content mutation counter. Increases exactly once per game
   * mutation that should eventually be represented by a projection/
   * checkpoint (see the central bumping `set` wrapper below). Never
   * wall-clock; never advances for UI-only/local-layout state. */
  localSeq: number;
  /** Persisted reconnect metadata scoped to the current (code, sessionId).
   * Null means "no evidence" — a legacy v11 store, or no scope established
   * yet. See src/firebase/reconnectDecision.ts. */
  sync: SyncMeta | null;

  newGame: (scriptId: string, opts?: NewGameOpts) => void;
  dealRolePool: () => SetupCommandResult;
  /** Explicitly publishes the private Deal to players. Never required to be
   * online: a local/offline game still records that the Storyteller
   * completed the initial reveal step. See revealReadiness.ts. */
  revealRoles: () => SetupCommandResult;
  // --- Pre-Reveal Setup refinement (Phase 9 Setup finalization B3) -------
  // Administration of the private Deal only — available strictly between a
  // completed initial Deal and a completed initial Reveal (see
  // canRefineSetup in features/setup/setupRefinement.ts). Distinct from the
  // generic assignRole()/setShownRole(), which remain unchanged and keep
  // serving later in-game character-change mechanics.
  /** Fresh random redistribution of the current ordinary dealt bag across
   * occupied ordinary players. Same characters, new assignment. */
  shuffleSetupRoles: () => SetupCommandResult;
  /** Exchanges the private role assignments of exactly two occupied,
   * non-Traveler ordinary players. Composition-neutral. */
  swapSetupRoles: (playerIdA: PlayerId, playerIdB: PlayerId) => SetupCommandResult;
  /** Changes exactly one ordinary player's actual role to a fresh
   * assignment, without touching any other player. May leave the setup
   * composition-invalid — that is preserved as explicit Storyteller intent
   * and surfaced by the analyzer rather than silently corrected. */
  replaceSetupRole: (playerId: PlayerId, roleId: RoleId) => SetupCommandResult;
  /** Applies a fully-staged edited bag (exactly one role per occupied
   * ordinary seat). Preserves every existing assignment whose role
   * occurrence survives in the new bag; only seats whose occurrence was
   * removed are reassigned, deterministically, to the newly added
   * occurrences. Never a full reshuffle. */
  applyEditedBag: (stagedRoleIds: RoleId[]) => SetupCommandResult;
  beginNightOne: () => SetupCommandResult;
  setPlannedPlayerCount: (count: number) => void;
  setRolePool: (roles: RoleId[]) => void;
  endGame: () => void;
  /**
   * Phase 10G (PHASE10G Section 17): the TERMINAL Finish Game of a game in
   * live play (Night/Day). The game is retained as a read-only final snapshot
   * (`phase: "ended"`, its final Life / Effect / Reminder / Role / Alignment /
   * Rule Fact state frozen); Undo is cleared, so nothing can restore Night/Day;
   * any lobby association (and its reconnect metadata) is detached, so a
   * reload never reconnects the snapshot to its terminated session. No winner
   * or result is inferred or stored.
   *
   * Refused (nothing changes) for Setup (discard/replacement is a separate
   * concept), for an already-ended game, and while a multiplayer lobby is
   * still attached: the caller closes the session authoritatively first
   * (closeMultiplayerSession clears the lobby only on success), so a failed
   * close always leaves the game live and unchanged.
   *
   * Phase 10H (contract §§14-15; amends 10G §§17.2/19): the ONE terminal seam
   * for every explicit terminal intent. Declare Good / Declare Evil store a
   * Game Result `{ winner, declaredAt: <the live Game Moment> }` on the ended
   * snapshot; End Without Result stores none. Nothing is inferred. When the
   * session was already ended (`recovery`, read back after a lost response),
   * its confirmed outcome -- a declared result OR a confirmed absence of one
   * -- is what is retained, whatever this retry's intent, so the local record
   * never disagrees with the players'. Clears any terminal-close lock in the
   * same commit; Undo-free.
   */
  finishGame: (intent: TerminalIntent, recovery?: TerminalRecovery) => SetupCommandResult;
  /** Phase 10H (contract §15 step 2-3): capture the terminal intent and lock
   * gameplay mutation for the authoritative close. Refused (false) without a
   * game in Night/Day or while another close is in flight. */
  beginTerminalClose: (intent: TerminalIntent) => boolean;
  /** Phase 10H (contract §15 step 12): the remote terminal close failed --
   * release the mutation lock, keep the game live, keep the intent for retry. */
  failTerminalClose: (message: string) => void;
  /** Phase 10H: abandon a failed (not in-flight) terminal close. */
  clearTerminalClose: () => void;
  setView: (view: "home" | "game" | "newgame") => void;
  selectPlayer: (id: PlayerId | null) => void;
  addCustomScript: (script: Script) => AddScriptResult;
  removeCustomScript: (id: string) => void;
  /**
   * Attaches (or, with null, detaches) the multiplayer lobby. ASTRA-10G-001:
   * attaching is refused -- returns false, nothing changes -- while the
   * current game is ended: a finished game can never acquire a lobby, however
   * late an asynchronous Go Live continuation arrives. Detaching always works.
   */
  setLobby: (lobby: LobbyConnection | null) => boolean;
  setLobbyStatus: (status: LobbyConnection["status"]) => void;
  setPendingKnocks: (knocks: { uid: string; name: string }[]) => void;
  seatPlayerFromKnock: (uid: string, name: string) => PlayerId | null;
  bindRosterUid: (uid: string, playerId: PlayerId) => void;
  addToPendingQueue: (uid: string, name: string) => void;
  /** Phase 9R.2 (Astra R1): `participantId`, when given, is the identity the
   * membership seating command already recorded server-side for this
   * binding (rosterParticipants/{uid}); it must be a NEW participation
   * instance -- one this game has never used -- or the command refuses. */
  assignPendingToSeat: (uid: string, seatPlayerId: PlayerId, participantId?: ParticipantId) => boolean;
  /** Phase 9R.2 (Astra R1): recovery-only. Occupies an empty seat for a live
   * membership binding whose authoritative server record
   * (rosterParticipants/{uid}) names the participation instance it seats,
   * preserving that exact ParticipantId -- used when the recovered game's
   * own occupant of the seat is not that instance (a stale checkpoint or
   * stale local game) or the seat is empty there. Refuses when the seat is
   * not an existing empty seat, the name is blank, or another current
   * occupant already holds that ParticipantId. Never consults history,
   * names, or UIDs to decide identity. */
  restoreSeatedMember: (uid: string, seatPlayerId: PlayerId, name: string, participantId: ParticipantId) => boolean;
  removePendingPlayer: (uid: string) => void;

  addPlayer: (name: string) => void;
  /** Fill the first planned empty seat, falling back to a new seat when none exist. */
  addPlayerToSeat: (name: string) => void;
  /** Add one deliberate empty planned seat. */
  addEmptySeat: () => void;
  /** Add planned Traveler capacity: an ordinary-neutral empty seat plus the
   * matching plan growth (Phase 9 Setup finalization, Section 3.E). Never
   * pre-flags the seat itself isTraveler -- actual designation happens
   * through setIsTraveler once a real player occupies it. */
  addTravelerSeat: () => void;
  /** Remove a player and its seat locally. Membership is revoked by the command layer first. */
  removePlayer: (id: PlayerId) => boolean;
  /** Turn a seated player into an empty seat locally. Membership is revoked first. */
  unseatPlayer: (id: PlayerId) => boolean;
  renamePlayer: (id: PlayerId, name: string) => void;
  setSeatOrder: (order: PlayerId[]) => void;
  movePlayer: (id: PlayerId, direction: "left" | "right") => void;

  // --- Phase 10D: Roles and perception ----------------------------------------
  /** THE authoritative Role/perception writer. Plans one atomic
   * RoleTransaction (roleResolution.ts) -- ordered, participant-bound Actual
   * Role changes/corrections and explicit perception changes -- and commits an
   * accepted plan as exactly one game replacement: partial-field patches of
   * every affected participant plus Role History, one Undo entry, one
   * localSeq step. A refusal or true no-op changes nothing. Every Role and
   * perception command below wraps this; a future ability engine submits its
   * resolved intents here too. Setup construction (Deal/Shuffle/Swap/Manual
   * Override/Edit Bag, Traveler designation) stays Setup-specific. */
  resolveRoles: (transaction: RoleTransaction) => RoleCommandResult;
  /** Compatibility adapter over one gameplay Actual Role change, bound to
   * whoever occupies `id` right now. Never changes ordinary-vs-Traveler status
   * (the seam does; this adapter keeps its long-standing meaning) and never
   * clears a Role: an empty destination is refused. Optional Mutation Context
   * supplies Provenance for the Role History Record during Live Play. */
  assignRole: (id: PlayerId, roleId: RoleId, context?: MutationContext) => RoleCommandResult;
  /** Compatibility adapter over one Actual Role CORRECTION (see
   * CorrectActualRoleIntent): abilityUsed is preserved; Live Play History
   * carries `correction: true`. */
  correctRole: (id: PlayerId, roleId: RoleId, options?: { travelerArrivalPolicy?: TravelerArrivalPolicy }, context?: MutationContext) => RoleCommandResult;
  /** Compatibility adapter over one explicit perception change bound to
   * whoever occupies `id` now: shows the Actual Role (a no-op for a Role that
   * needs an explicit shown identity, e.g. a concealed one). */
  showAssignedRole: (id: PlayerId) => RoleCommandResult;
  /** Perception adapters: each is one setPerception bundle. Re-selecting the
   * identical bundle is a true no-op -- nothing cleared, withdrawn or
   * recorded. A different Shown Role derives its alignment (null) unless the
   * role is unchanged. */
  setShownRole: (id: PlayerId, roleId: RoleId | null) => RoleCommandResult;
  setShownAlignment: (id: PlayerId, alignment: ShownAlignment | null) => RoleCommandResult;
  setBehaviorMode: (id: PlayerId, mode: BehaviorMode) => RoleCommandResult;
  setPerception: (
    id: PlayerId,
    perception: { shownRole: RoleId | null; shownAlignment: ShownAlignment | null; behaviorMode?: BehaviorMode },
    context?: MutationContext,
  ) => RoleCommandResult;
  setBluffs: (id: PlayerId, bluffs: RoleId[]) => void;
  setFakeMinions: (id: PlayerId, playerIds: PlayerId[]) => void;
  setPrivateText: (id: PlayerId, text: string) => void;
  /** Explicit ordinary<->Traveler conversion for an occupied seat (Phase 9
   * Setup finalization B4, revised). Converting ordinary->Traveler is
   * refused if it would drop occupied ordinary players below MIN_PLAYERS;
   * converting Traveler->ordinary is refused if it would raise occupied
   * ordinary players above MAX_PLAYERS. On success, plannedTravelerCount
   * (and therefore the ordinary composition target) immediately snaps to
   * match the new occupancy -- no separate planned-vs-seated reconciliation
   * action is ever required. Never restarts the game or lobby, and never
   * touches any other player's role. */
  setIsTraveler: (id: PlayerId, isTraveler: boolean) => SetupCommandResult;
  // --- Phase 10E: Actual Alignment -------------------------------------------
  /** THE authoritative Actual Alignment writer. Plans one atomic
   * AlignmentTransaction (alignmentResolution.ts) -- participant-bound
   * gameplay changes OR corrections (never mixed), at most one per
   * participant -- and commits an accepted plan as exactly one game
   * replacement: partial-field patches of every affected participant (plus a
   * Traveler's packet withdrawal / fresh epoch / Demon-draft cleanup) and
   * Alignment History, one Undo entry, one localSeq step. A refusal or true
   * no-op changes nothing. Never writes perception (setPerception owns it).
   * New render-bound UI calls this directly; a future ability engine submits
   * its resolved intents here too. */
  resolveAlignments: (transaction: AlignmentTransaction) => AlignmentCommandResult;
  /**
   * Phase 10F: the ONE Storyteller-owned ability command (PHASE10F Section
   * 3.4). Synchronously plans the request against the CURRENT game with the
   * pure coordinator (planAbilityResolution -- which revalidates the workflow
   * fingerprint and every participant binding right here, immediately before
   * commit) and commits an accepted plan as exactly one game replacement, one
   * Undo entry, one localSeq step and so one projection cycle. A refusal at
   * any internal stage, or a true no-op, commits nothing. It never calls
   * another store command. `semantics` defaults to the canonical registry.
   */
  resolveAbility: (request: AbilityResolutionRequest, semantics?: AbilitySemanticsRegistry) => AbilityCommandResult;
  /**
   * Phase 10G: THE Storyteller-owned Game Rule Fact command (PHASE10G Section
   * 4.4). Plans one ordered transaction with the pure planner
   * (gameRuleFacts.ts), proves the result persistable (schema, Firebase write
   * compatibility, recovery-checkpoint envelope -- Section 4.5) and commits a
   * changed plan as exactly one game replacement, one Undo entry and one
   * localSeq step. Refused in Setup and in an ended game. A fact produced by an
   * ability resolution goes through resolveAbility instead, never here.
   */
  resolveGameRuleFacts: (transaction: GameRuleFactTransaction) => GameRuleFactCommandResult;
  /** Compatibility adapter: one gameplay Actual Alignment change of a
   * TRAVELER, bound to whoever occupies `id` right now (refused for an
   * ordinary participant). Routes through resolveAlignments; callers may
   * ignore the result. Optional Mutation Context supplies Provenance. */
  setTravelerAlignment: (id: PlayerId, alignment: Alignment, context?: MutationContext) => AlignmentCommandResult;
  /** Compatibility adapter: one gameplay Actual Alignment change of any
   * participant, bound to whoever occupies `id` right now and the alignment /
   * Traveler status observed at call time. Routes through resolveAlignments;
   * never inferred, never called automatically. */
  setActualAlignment: (id: PlayerId, alignment: Alignment, context?: MutationContext) => AlignmentCommandResult;
  prepareTravelerDemon: (id: PlayerId) => void;
  completeTravelerInformation: (id: PlayerId) => void;
  completeTravelerArrivalCheck: (id: PlayerId) => void;
  /** @deprecated Phase 10A compatibility adapter -- exactly
   * recordExile(id, "died"). New callers use recordExile. */
  exileTraveler: (id: PlayerId) => void;
  setFabled: (fabled: RoleId[]) => void;
  setLorics: (lorics: RoleId[]) => void;

  // --- Phase 10A: Life State ------------------------------------------------
  /** THE authoritative life writer. Plans one atomic LifeTransaction
   * (lifeResolution.ts) -- one or more already-resolved life intents,
   * optionally correlated by `resolutionId` -- and commits an accepted plan
   * as exactly one game replacement: Current State patch, Life Event Window
   * update and one History mirror per affected participant, one Undo entry,
   * one localSeq step. A refusal or true no-op changes nothing. Every other
   * Life command below is a one-intent wrapper around this; a future
   * ability engine submits its resolved intents here too. */
  resolveLife: (transaction: LifeTransaction) => LifeCommandResult;
  /** A death not represented by an execution/exile. Night or Day. */
  recordDeath: (id: PlayerId, context?: MutationContext) => LifeCommandResult;
  /** The actual executee's execution (Day only). Extra executions and a
   * Traveler executee need the planner's explicit confirmation tokens,
   * bound to the participant and Game Moment (10A-ASTRA-002). */
  recordExecution: (
    id: PlayerId,
    outcome: ExecutionOutcome,
    options?: { confirmations?: readonly LifeConfirmationToken[] },
    context?: MutationContext,
  ) => LifeCommandResult;
  /** A Traveler exile (Day only); may be survived. */
  recordExile: (id: PlayerId, outcome: ExileOutcome, context?: MutationContext) => LifeCommandResult;
  /** A true resurrection (not a correction): alive, vote held, exile
   * cleared, ability restored. */
  resurrect: (id: PlayerId, context?: MutationContext) => LifeCommandResult;
  spendGhostVote: (id: PlayerId, context?: MutationContext) => LifeCommandResult;
  restoreGhostVote: (id: PlayerId, context?: MutationContext) => LifeCommandResult;
  /** Status-only correction -- no Life Event, never restores an ability. */
  correctLifeStatus: (id: PlayerId, target: LifeStatusTarget, context?: MutationContext) => LifeCommandResult;
  /** Corrections of an event still in the Life Event Window. `repair`
   * status corrections are committed atomically in the same transaction --
   * Current State is never repaired implicitly. */
  retractLifeEvent: (eventId: LifeEventId, repair?: RepairTarget[], context?: MutationContext) => LifeCommandResult;
  amendLifeEvent: (eventId: LifeEventId, replacement: LifeEventSpec, repair?: RepairTarget[], context?: MutationContext) => LifeCommandResult;
  /** Records a forgotten event at the immediately previous phase. */
  lateRecordLifeEvent: (event: LifeEventSpec & { playerId: PlayerId }, repair?: RepairTarget[], context?: MutationContext) => LifeCommandResult;
  /** @deprecated Phase 10A compatibility adapter for untouched callers:
   * `false` is recordDeath; `true` is a status-only correction to alive
   * (never a resurrection). Refused outside Night/Day like every life
   * command. New code uses the semantic commands above. */
  setAlive: (id: PlayerId, alive: boolean, context?: MutationContext) => void;
  /** @deprecated Phase 10A compatibility adapter: `false` is
   * spendGhostVote, `true` is restoreGhostVote -- both legal only while
   * dead. */
  setGhostVote: (id: PlayerId, ghostVote: boolean, context?: MutationContext) => void;
  /** Phase 10F: an adapter over resolveLife (useAbility / correctAbilityUsed). */
  setAbilityUsed: (id: PlayerId, used: boolean, context?: MutationContext) => LifeCommandResult;
  // --- Phase 10B: Effects -----------------------------------------------------
  /** THE authoritative Effect writer. Plans one atomic EffectTransaction
   * (effectResolution.ts) -- one or more ordered, already-resolved Effect
   * intents, gameplay or correction, optionally correlated by
   * `resolutionId` -- and commits an accepted plan as exactly one game
   * replacement: every affected participant's effects[] plus History, one
   * Undo entry, one localSeq step. A refusal or true no-op changes nothing.
   * Every other Effect command below wraps this; a future ability engine
   * submits its resolved intents here too. */
  resolveEffects: (transaction: EffectTransaction) => EffectCommandResult;
  /** The Storyteller quick control: turns exactly the deterministic manual
   * Effect `manual:<type>` of the bound participant on or off -- never any
   * other Effect of the same type. `on` for a suppressed manual Effect
   * resumes it. */
  setManualEffect: (target: EffectParticipantBinding, type: string, on: boolean, context?: MutationContext) => EffectCommandResult;
  /** @deprecated Phase 10B compatibility adapter: setManualEffect bound to
   * whoever occupies `id` right now. */
  setStatus: (id: PlayerId, status: string, on: boolean) => void;
  /** @deprecated Phase 10B compatibility adapter over the Apply intent
   * (bound to the current occupant and, for `sourcePlayer`, the current
   * source occupant). Apply semantics: returns the id when the Effect was
   * created or an identical one already exists; null when refused --
   * including different content under an existing id (never an upsert), a
   * timed lifetime during Setup, or an `appliedAt` other than now. */
  addEffect: (id: PlayerId, effect: EffectInput) => EffectId | null;
  /** @deprecated Phase 10B compatibility adapter over the Remove intent:
   * removes exactly that one Effect instance. */
  removeEffect: (id: PlayerId, effectId: EffectId) => void;
  // --- Phase 10C: Reminders ----------------------------------------------------
  /** THE Reminder writer. Plans one atomic ReminderTransaction
   * (reminderResolution.ts) -- ordered place/amend/remove intents, gameplay
   * or correction, bound to the participation instances the caller observed
   * -- and commits an accepted plan as exactly one game replacement: every
   * affected participant's reminders[] plus History, one Undo entry, one
   * localSeq step. A refusal or true no-op changes nothing. Reminders are
   * non-authoritative notation: nothing mechanical ever reads them. */
  resolveReminders: (transaction: ReminderTransaction) => ReminderCommandResult;
  /** Compatibility adapter over one Place intent, bound to whoever occupies
   * `id` now (and, for `sourcePlayer`, the current source occupant). Place
   * semantics: returns the id when placed or when an identical Reminder
   * already exists under it; null when refused -- including different
   * content under an existing id (never an upsert), an ended game, or any
   * smuggled durable field (sourceParticipant, createdAt, cleanupCue...). */
  addReminder: (id: PlayerId, reminder: ReminderInput) => ReminderId | null;
  /** Compatibility adapter over one Remove intent: removes exactly that one
   * Reminder instance of the current occupant. */
  removeReminder: (id: PlayerId, reminderId: ReminderId) => ReminderCommandResult;
  /** Phase 9D.3: the single Authoritative Information command. Resolves
   * the Recipient's Actual Role and its Information Actions from Role
   * data (never a Role-id branch), structurally validates the supplied
   * Information Values against that Action's Information Requirements,
   * and -- only if valid -- creates exactly one Information Delivery
   * Record snapshotting the Actual Role, Game Moment, and values.
   * Structurally valid Storyteller-provided information is always stored
   * as given; this never recalculates or rejects it based on Current
   * State. */
  recordInformationDelivery: (
    recipientPlayerId: PlayerId,
    informationActionId: InformationActionId,
    values: InformationValue[],
    context?: MutationContext
  ) => RecordInformationDeliveryResult;
  /** Phase 9D.3: removes exactly one Information Delivery Record. A
   * correction is remove-then-recordInformationDelivery again; this never
   * touches unrelated Current State. */
  removeInformationDelivery: (deliveryId: InformationDeliveryId) => void;
  /** Phase 10G (Section 20.1): changed notes over MAX_ST_NOTES are refused
   * (never truncated); an unchanged legacy value of any length is a no-op. */
  setNotes: (id: PlayerId, notes: string) => SetupCommandResult;

  /** Setup -> Night/Day delegates to beginNightOne(); Night <-> Day
   * delegates to advancePhase() (live time is monotonic: Night N -> Day N,
   * Day N -> Night N+1); Night/Day -> Setup is refused (Setup is pre-game
   * only); leaving "ended" is refused. */
  setPhase: (phase: StorytellerLobbyRecord["phase"]) => SetupCommandResult;
  advancePhase: () => SetupCommandResult;

  setNightStepStatus: (day: number, stepKey: string, status: NightStepStatus) => void;
  /** Phase 10G (Section 20.2): changed notes over MAX_NIGHT_STEP_NOTES are
   * refused (never truncated); unchanged notes are a no-op. */
  setNightStepNotes: (day: number, stepKey: string, notes: string) => SetupCommandResult;
  clearNightProgress: (day: number) => void;

  undo: () => void;

  setGrimoireMode: (mode: GrimoireMode) => void;
  setTokenPosition: (id: PlayerId, x: number, y: number) => void;
  clearTokenPositions: () => void;

  // --- Phase 9C.2A (OPUS-001) reconnect/acknowledgement plumbing ---------
  /** Establish (or preserve) sync metadata for exactly this scope. A no-op
   * when `sync` already matches (code, sessionId) — creating a replacement
   * SessionWriter must never overwrite unresolved `lastAttempt` evidence
   * for the same scope. Only replaces `sync` outright on a genuine scope
   * change (a different lobby/session than what's currently tracked). */
  ensureSyncScope: (code: string, sessionId: string) => void;
  /** Record that a writer commit was attempted with this exact
   * token/revision pair, for this scope. Does not touch `game` — writer
   * attempts are not game-content mutations. */
  noteWriterAttempt: (code: string, sessionId: string, guard: GuardStamp) => void;
  /** Record that a writer commit genuinely succeeded while the writer
   * remained active, for this scope. Advances `ackedGuard` and reconciles
   * a matching `lastAttempt`. Also used to promote the accepted guard when
   * reconnect recognizes a lost acknowledgement. Does not touch `game`. */
  noteWriterAck: (code: string, sessionId: string, guard: GuardStamp) => void;
  /** Advance `ackedGameSeq` to exactly `seqAtFlush` — the localSeq value
   * captured at the same moment the flushed game snapshot was captured —
   * once that specific projection flush has genuinely succeeded while the
   * writer remained active. Never advances past what was actually
   * acknowledged, and never regresses. Does not touch `game`. */
  acknowledgeGameFlush: (code: string, sessionId: string, seqAtFlush: number) => void;
  /** Durably promotes a recognized lost acknowledgement (Finding B1) to
   * accepted baseline state. Reconnect can recognize, from `lastAttempt`
   * alone, that a commit genuinely landed on the server but was never
   * acknowledged locally — but until THIS is called, that fact survives
   * only as `lastAttempt`, which any later writer attempt is free to
   * overwrite before the recovery is durable. Call synchronously, with no
   * await in between, right after recognizing the recovery and before any
   * further await that could let another writer attempt allocate a new
   * revision (see storytellerSync.ts's reconnect call site).
   *
   * Verifies scope; verifies `lastAttempt` exactly equals `guard` (else a
   * no-op — never promotes a mismatched or already-superseded attempt);
   * never lowers `ackedGuard.revision`; sets `ackedGuard = guard`; clears
   * `lastAttempt`. Deliberately never touches `ackedGameSeq`: the recovered
   * commit could equally have been a projection flush or a membership-only
   * write, and without durable seq-to-guard evidence distinguishing those,
   * only under-reporting local dirtiness would be safe to skip — so this
   * never advances it. Over-reporting dirty state is acceptable; falsely
   * reporting clean is not. */
  promoteRecoveredAck: (code: string, sessionId: string, guard: GuardStamp) => void;
  /** Atomically replace `game` with a validated remote checkpoint, clear
   * undo (because remote state was deliberately accepted), and make the
   * restored game clean with respect to the current local sequence by
   * setting `ackedGameSeq` to it and `ackedGuard` to the newly accepted
   * remote guard (null only in the legacy/no-writeGuard-yet edge case).
   * Also clears `lastAttempt` unconditionally (Luna review, Finding 1):
   * once a remote checkpoint has been deliberately accepted — explicitly
   * or automatically — any unresolved attempt evidence from the prior
   * local lineage is no longer meaningful and must not survive to be
   * mistaken for a lost acknowledgement against a later, different remote
   * guard (including one that represents a genuine server rewind relative
   * to the newly accepted baseline). This one replacement is deliberately
   * exempt from the general "a `game` change bumps localSeq" rule:
   * adopting remote content is the opposite of new local intent. */
  restoreRemoteCheckpoint: (game: StorytellerLobbyRecord, guard: GuardStamp | null) => void;
};

/** Phase 10C: the fields addReminder's caller-facing input may carry
 * (ReminderInput). Anything else is refused by key presence. */
const REMINDER_INPUT_KEYS: ReadonlySet<string> = new Set(["id", "label", "sourcePlayer", "sourceCharacter", "note", "cleanup"]);

/** A status-only repair committed alongside an event correction. */
export type RepairTarget = { playerId: PlayerId; target: LifeStatusTarget };

const repairIntents = (repair: RepairTarget[] | undefined): LifeIntent[] =>
  (repair ?? []).map(({ playerId, target }) => ({ kind: "correctStatus", playerId, target }));

const pushUndo = (
  game: StorytellerLobbyRecord | null,
  stack: StorytellerLobbyRecord[]
): StorytellerLobbyRecord[] => {
  if (!game) return stack;
  const next = [...stack, clone(game)];
  if (next.length > UNDO_LIMIT) next.shift();
  return next;
};

/**
 * Phase 9R.4 (B8): a command that would leave authoritative state unchanged
 * must return before pushUndo()/set() -- the central wrapper bumps localSeq
 * for any new game reference, so the decision belongs to the command. These
 * two helpers are deliberately narrow (one stored list, one player record),
 * never a whole-game comparison.
 *
 * Order-sensitive equality for a stored id list: every list compared with
 * it (seatOrder, fabled, lorics, rolePool) stores its order as given.
 */
const sameList = <T,>(a: readonly T[], b: readonly T[] | undefined): boolean =>
  !!b && a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * True when `next` -- a command's COMPLETE intended result for one player,
 * dependent resets included -- equals `existing` apart from the packetEpoch
 * that invalidatePrivatePacket() always mints. That nonce only marks that
 * something else about the perception changed; minting it when nothing
 * else did would be manufactured invalidation. Any other difference (a
 * cleared override, deleted privateInfo, a withdrawn publishedPacket, a
 * reset travelerArrival) is a real mutation.
 */
const samePlayerApartFromEpoch = (existing: STPlayerRecord, next: STPlayerRecord): boolean =>
  sameSnapshot({ ...next, packetEpoch: existing.packetEpoch }, existing);

/**
 * Phase 9R.4 (B8 remediation): the one way a command resolves a
 * caller-supplied PlayerId to a player. `game.players` is a plain object,
 * so an indexed `game.players[id]` also "finds" inherited Object.prototype
 * members -- "toString"/"constructor" resolve to functions and "__proto__"
 * to Object.prototype itself, all truthy -- and a later
 * `{ ...game.players, [id]: ... }` would mint a bogus own record for them.
 * A PlayerId names a player only when it is an OWN property, the same rule
 * participantRefOf() already applies. Commands resolve through this before
 * computing anything, pushing Undo, or calling set().
 */
const ownPlayer = (game: Pick<StorytellerLobbyRecord, "players">, id: PlayerId): STPlayerRecord | undefined =>
  Object.prototype.hasOwnProperty.call(game.players, id) ? game.players[id] : undefined;

/**
 * Phase 10B: binds a live PlayerId to the participation instance occupying
 * it RIGHT NOW -- how the compatibility adapters (setStatus/addEffect/
 * removeEffect) centrally capture the ParticipantId the Effect planner
 * requires. Null for a nonexistent/inherited id or an empty seat. UI callers
 * bind the participant they rendered instead, so a seat replaced in between
 * is refused as stale.
 */
const currentBinding = (game: StorytellerLobbyRecord | null, id: PlayerId): EffectParticipantBinding | null => {
  const player = game && typeof id === "string" ? ownPlayer(game, id) : undefined;
  if (!player || player.isEmpty || !player.participantId) return null;
  return { playerId: player.id, participantId: player.participantId };
};

/** Phase 10D: the occupied player record at `id` right now (own property,
 * seated, with a participation identity) -- what the compatibility adapters
 * bind to. Undefined for a nonexistent/inherited id or an empty seat. */
const currentOccupant = (game: StorytellerLobbyRecord | null, id: PlayerId): STPlayerRecord | undefined => {
  const player = game && typeof id === "string" ? ownPlayer(game, id) : undefined;
  return player && !player.isEmpty && player.participantId ? player : undefined;
};
const notSeatedRefusal = (): RoleRefusal => ({ ok: false, code: "notSeated", message: "This player is not seated." });
const alignmentNotSeated = (): AlignmentRefusal => ({ ok: false, code: "notSeated", message: "This player is not seated." });

/**
 * Phase 9R.4 (B8 remediation #2): setSeatOrder() only REORDERS -- seat
 * creation and removal belong to addPlayer/addEmptySeat/addTravelerSeat/
 * removePlayer -- so its input must be an exact permutation of the current
 * authoritative seat ids: the same length, no duplicate, and every id an
 * own player record (ownPlayer) that is already seated. Membership in the
 * current seatOrder is checked explicitly, not inferred from length plus
 * existence, so an own record that is not seated can never be ordered in.
 */
const isSeatPermutation = (game: StorytellerLobbyRecord, order: readonly PlayerId[]): boolean => {
  if (!Array.isArray(order) || order.length !== game.seatOrder.length) return false;
  const seated = new Set(game.seatOrder);
  const seen = new Set<PlayerId>();
  for (const id of order) {
    if (seen.has(id) || !seated.has(id) || !ownPlayer(game, id)) return false;
    seen.add(id);
  }
  return true;
};

const patchPlayer = (
  game: StorytellerLobbyRecord,
  id: PlayerId,
  patch: Partial<STPlayerRecord>
): StorytellerLobbyRecord => {
  const existing = ownPlayer(game, id);
  if (!existing) return game;
  return {
    ...game,
    players: {
      ...game.players,
      [id]: { ...existing, ...patch },
    },
  };
};

/**
 * A fresh private assignment for `role`: the same reset dealRolePool()
 * performs per seat. Deceptive configuration from a previous assignment
 * must never follow a player into an unrelated new one — normal roles get
 * their deterministic shown identity, concealed roles become unresolved,
 * role-specific private info/packets are cleared. Shared by Deal and every
 * pre-Reveal Setup refinement command (Shuffle/Swap/Manual Override/Edit
 * Bag Apply) so this reset has exactly one implementation.
 */
const freshAssignment = (existing: STPlayerRecord, role: RoleId, registry: RoleRegistry): STPlayerRecord => {
  const next: STPlayerRecord = { ...existing, ...dealtIdentity(role, registry), abilityUsed: false };
  delete next.privateInfo;
  return invalidatePrivatePacket(next);
};

/**
 * PHASE10G Section 4.5 / Section 21: the persistence preflight of a NEW 10G
 * authoritative command that can grow persisted state -- exactly the
 * SOL-10F-C3 / E1 proofs resolveAbility performs inline (unchanged). A
 * planned game becomes authoritative only if the production writer can
 * project it: the SAME Firebase write-compatibility check checkpoint recovery
 * uses, against the SAME Storyteller destination the writer writes (the live
 * lobby's, or -- before a room exists -- the canonical maximum code shape),
 * and the derived recovery checkpoint proven against the conservative
 * supported-roster envelope with the writer's own serializer. Null when safe;
 * otherwise the Storyteller-facing reason (nothing may be committed).
 */
const persistencePreflight = (planned: StorytellerLobbyRecord, current: StorytellerLobbyRecord, lobby: LobbyConnection | null): string | null => {
  const writable = validateFirebaseWritableValue(planned, storytellerPathSegments(lobby?.code ?? MAX_ROOM_CODE_SHAPE));
  if (!writable.ok) {
    return `This result cannot be safely stored online (the resulting game ${writable.message.replace(/\.$/, "")}) -- nothing was recorded.`;
  }
  const checkpoint = validateCheckpointEnvelope(planned, current);
  if (!checkpoint.ok) {
    return `This result cannot be safely stored online (the game's recovery checkpoint ${checkpoint.message.replace(/\.$/, "")}) -- nothing was recorded.`;
  }
  return null;
};

const CLEAN_STATE = { game: null, view: "home" as const, undoStack: [] as never[], customScripts: {}, lobby: null };

/** Phase 10F (v24): clears tonight's arrival / guided-wake steps of the
 * participation instance at `id` -- keyed by ParticipantId, never the seat. */
const resetTravelerNightProgress = (game: StorytellerLobbyRecord, id: PlayerId) => {
  const player = game.players[id];
  const participantId = player?.participantId;
  if (!player || !participantId) return game.nightProgress;
  // SOL-10F-A7: exact keys of THIS participant's own Role steps only.
  const exact = participantRoleStepEntries(game.day, participantId, [player.actualRole, player.shownRole, player.publicDisplayRole]);
  return Object.fromEntries(Object.entries(game.nightProgress).filter(([key]) => !exact.has(key)));
};

/** Phase 9R.1 Astra remediation (Finding M2): `undoStack` is untrusted
 * persisted-state data -- it may be genuinely absent (`undefined`), a
 * valid array, or a malformed PRESENT non-array value (e.g. `{}`, a
 * string, a number). Every migration step below that maps or spreads
 * `s.undoStack` previously assumed any truthy value was already an array;
 * `s.undoStack.map(...)` throws "is not a function" and a bare
 * `[...s.undoStack]` throws "is not iterable" for a malformed non-array
 * value instead of failing safely (`??` alone only substitutes for
 * `null`/`undefined`, never for a truthy non-array). A malformed
 * undoStack must never be silently normalized into `[]` here -- every
 * site below leaves it completely untouched when this guard fails, so the
 * final StorytellerStateSchema validation gate (which requires undoStack
 * to be an array or absent -- schemas.ts's
 * `z.array(StorytellerGamePersistedSchema).optional()`) rejects it and
 * triggers the existing reset path below, exactly the established
 * absent-vs-malformed distinction already applied to remote checkpoint
 * migration (see gameMigration.ts's own Finding A3 doc comment). */
function isMigratableUndoStack(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

export function migrateStoreState(state: unknown, fromVersion: number): unknown {
  const s = state as { game?: Record<string, unknown>; undoStack?: unknown; lobby?: unknown };
  // Phase 10C (ASTRA-10C-003): per-entry routing BEFORE any old step. A
  // game-shaped entry's OWN version/evidence -- not the outer persisted
  // envelope's -- decides which game-content migrations may touch it. Each
  // entry (Current State and every Undo snapshot, independently) is
  // classified once, from its untouched content, by the one canonical
  // detector (detectLegacyGameVersion: explicit marker first, then v21/v20/
  // v19/v17 evidence, then bounded structure). A game-content step targeting
  // version N runs on an entry only when BOTH the envelope and the entry
  // itself are older than N, so an old envelope can never "repair" a marked
  // current game (e.g. default a malformed `fabled: null`) or delete its
  // evidence (e.g. `startingNonTravelerCount`) before the entry's own marker
  // is honoured. A shape too old to detect keeps the envelope's version, so
  // genuine legacy saves migrate exactly as before. Envelope-only metadata
  // (lobby, localSeq/sync) is still migrated by the envelope version.
  const undoEntries: unknown[] = isMigratableUndoStack(s.undoStack) ? s.undoStack : [];
  const entryVersion = new Map<object, number>();
  for (const entry of [s.game, ...undoEntries]) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const detected = detectLegacyGameVersion(entry as Record<string, unknown>);
    entryVersion.set(entry, detected === null ? fromVersion : Math.max(fromVersion, detected));
  }
  const olderThan = (entry: unknown, version: number): entry is Record<string, unknown> =>
    !!entry && typeof entry === "object" && (entryVersion.get(entry) ?? Number.POSITIVE_INFINITY) < version;
  /** The game entry, when it is itself older than `version`. */
  const gameOlderThan = (version: number) => (olderThan(s.game, version) ? s.game : undefined);
  /** Every Undo entry that is itself older than `version`. */
  const undoOlderThan = (version: number) => undoEntries.filter((entry) => olderThan(entry, version)) as Record<string, unknown>[];

  if (fromVersion < 2) {
    const game = gameOlderThan(2);
    if (game && !game.nightProgress) game.nightProgress = {};
    for (const e of undoOlderThan(2)) {
      if (!e.nightProgress) e.nightProgress = {};
    }
  }
  if (fromVersion < 3) {
    const game = gameOlderThan(3);
    if (game) {
      if (!game.fabled) game.fabled = [];
      if (!game.bluffs) game.bluffs = [];
    }
    for (const e of undoOlderThan(3)) {
      if (!e.fabled) e.fabled = [];
      if (!e.bluffs) e.bluffs = [];
    }
  }
  if (fromVersion < 4) {
    const game = gameOlderThan(4);
    if (game && !game.lorics) game.lorics = [];
    for (const e of undoOlderThan(4)) {
      if (!e.lorics) e.lorics = [];
    }
  }
  if (fromVersion < 5) {
    const game = gameOlderThan(5);
    if (game) {
      if (!game.rolePool) game.rolePool = [];
      if (game.plannedPlayerCount === undefined) game.plannedPlayerCount = 0;
    }
    for (const e of undoOlderThan(5)) {
      if (!e.rolePool) e.rolePool = [];
      if (e.plannedPlayerCount === undefined) e.plannedPlayerCount = 0;
    }
  }
  // v6: grimoireMode and tokenPositions added — both are top-level optional
  // fields with Zod defaults, so no data migration is needed; old persisted
  // states pass validation and receive undefined → initial state provides "ring"/{}.

  if (fromVersion < 7) {
    const game = gameOlderThan(7);
    if (game) {
      if (!game.pendingPlayers) game.pendingPlayers = {};
      const players = game.players as Record<string, Record<string, unknown>> | undefined;
      if (players) {
        for (const p of Object.values(players)) {
          if (p.isEmpty === undefined) p.isEmpty = false;
        }
      }
    }
    for (const e of undoOlderThan(7)) {
      if (!e.pendingPlayers) e.pendingPlayers = {};
    }
  }

  if (fromVersion < 8 && s?.lobby) {
    // Envelope (lobby) + game content (Undo dropped, game code/uid reset).
    // ASTRA-10C-003: this step cannot run without altering or discarding
    // game entries, so if any entry is itself v8 or newer the envelope is an
    // impossible hybrid -- fail closed rather than rewrite current state.
    const entries = [s.game, ...undoEntries].filter((entry) => !!entry && typeof entry === "object");
    if (entries.some((entry) => !olderThan(entry, 8))) {
      // eslint-disable-next-line no-console
      console.warn("[migrate] a pre-v8 envelope holds a newer game entry; resetting rather than rewriting it");
      _migrationResetFlag = true;
      return CLEAN_STATE;
    }
    s.lobby = null;
    s.undoStack = [];
    if (s.game) { s.game.code = ""; s.game.storytellerUid = "local"; }
  }
  // v10 adds an optional starting population. Earlier snapshots cannot prove
  // this history; never derive it from their current attendance or roles.
  if (fromVersion < 10) {
    for (const entry of [gameOlderThan(10), ...undoOlderThan(10)]) {
      if (entry) delete entry.startingNonTravelerCount;
    }
  }
  // v11 introduces optional current Traveler facts. Leave legacy alignment,
  // completion and exile unknown. Public character can be recovered from truth.
  if (fromVersion < 11) {
    for (const entry of [gameOlderThan(11), ...undoOlderThan(11)]) {
      const players = (entry as { players?: Record<string, STPlayerRecord> } | undefined)?.players;
      for (const p of Object.values(players ?? {})) {
        if (p.isTraveler) p.publicDisplayRole = publicTravelerRole(p)?.id ?? null;
      }
    }
  }
  // v12 (Phase 9C.2A, OPUS-001) introduces localSeq/sync reconnect
  // watermarks. A legacy v11 store never tracked them: initialize localSeq
  // safely at 0 and leave sync null. Never invent acknowledgement evidence
  // from a legacy game's prior content, timestamps, or size — a valid
  // remote checkpoint outranks unevidenced legacy local state (see
  // reconnectDecision's "no sync metadata" rule). Envelope-only.
  if (fromVersion < 12) {
    const withSync = s as { localSeq?: number; sync?: unknown };
    withSync.localSeq = 0;
    withSync.sync = null;
  }
  // v13 (Phase 9 Setup finalization B4) introduces plannedTravelerCount, the
  // intended Traveler count out of plannedPlayerCount. A legacy game never
  // planned any Travelers explicitly -- default to 0, mirroring
  // plannedPlayerCount's own v5 migration default.
  if (fromVersion < 13) {
    for (const entry of [gameOlderThan(13), ...undoOlderThan(13)]) {
      if (entry && entry.plannedTravelerCount === undefined) entry.plannedTravelerCount = 0;
    }
  }
  // v14 (Phase 9D.1) -> v16 (Phase 9D.3): the structured Current State
  // (Actual Alignment, Effects, Reminders), History, and Information
  // Delivery evolution. Phase 9R.1 (Finding B1) extracted the actual
  // per-entry transformation into migrateGameEntry (gameMigration.ts) so
  // remote checkpoint recovery (readCheckpoint in storytellerSync.ts) can
  // apply the exact same rules to a bare remote game -- never a second,
  // divergent copy of them. See that function's own doc comment for what
  // each version step does and does not invent.
  //
  // v17 (Phase 9R.2): historical participant identity -- the same shared
  // per-entry migration, applied identically to Current State and every
  // Undo snapshot (deterministic legacy-current ParticipantIds, never
  // random; every v16 historical PlayerId reference becomes an unresolved
  // legacy ParticipantRef, never the current occupant's identity).
  //
  // v18 (terminology audit): the History category for an Actual Role change
  // is renamed from "identity" to "role" -- the same shared per-entry
  // migration, so Current State and every Undo snapshot canonicalize
  // identically. Only that one category value changes. A store already
  // labeled v18 never reaches this block (fromVersion is not < 18), so a
  // stale "identity" in it fails the schema gate below and resets rather
  // than being treated as v17 data.
  //
  // v19 (Phase 10A): the Life Event Window -- the same shared per-entry
  // migration adds it to Current State and to every Undo snapshot, each with
  // coverage from ITS OWN phase (never backfilled from History). A store
  // already labeled v19 never reaches this block, so a v19 game missing or
  // carrying a malformed window fails the schema gate below and resets
  // rather than being treated as v18 data. Within this block, any entry that
  // already carries v19 evidence is likewise left for the gate to judge.
  //
  // v20 (Phase 10B): the authoritative Effect lifecycle and explicit game
  // schema version evidence -- the same shared per-entry migration, applied
  // identically to Current State and every Undo snapshot (never consulting
  // History). An entry that already carries v20 evidence is left for the
  // schema gate below to judge, never "repaired".
  //
  // v21 (Phase 10C): non-authoritative Reminder notation -- the same shared
  // per-entry migration. An entry marked 20 receives v20 -> v21.
  //
  // v22 (Phase 10D): authoritative Role transitions -- the same shared
  // per-entry migration, a stamp on top of v21.
  //
  // v23 (Phase 10E): Alignment transitions -- the same shared per-entry
  // migration: a Traveler's inert explicit Shown Alignment is normalized to
  // Normal (null). Routing is per entry (Current State and every Undo snapshot
  // independently, exactly like remote checkpoint recovery): marker 20 -> v20
  // -> v21 -> v22 -> v23; marker 21 -> v21 -> v22 -> v23; marker 22 -> v22 ->
  // v23; marker 23 -> nothing; an older marker carrying newer evidence, a
  // marker-less entry carrying v20+ evidence, or any malformed marker receives
  // nothing and is rejected by the schema gate below. A marker-less genuine
  // legacy entry runs the whole chain through v23.
  //
  // v24 (Phase 10F): guided ability resolution -- the same shared per-entry
  // migration: marker 23 -> v24 drops ambiguous seat-addressed participant
  // Night progress (never re-keyed onto the current occupant) and stamps 24;
  // Information Delivery is preserved exactly (no performedRole/resolutionId
  // is invented). Every older marker continues the chain through v24; an
  // older marker carrying v24 evidence receives nothing and is rejected.
  //
  // v25 (Phase 10G): game-scoped Rule Facts -- the same shared per-entry
  // migration: marker 24 -> v25 initializes an EMPTY `gameRuleFacts`
  // collection and stamps 25 (no fact, History, delivery or participant state
  // is invented or rewritten). Every older marker continues the chain through
  // v25; an older marker carrying v25 evidence receives nothing and is
  // rejected.
  //
  // v26 (Phase 10H): reveal tokens and the declared Game Result -- the same
  // shared per-entry migration: marker 25 -> v26 is a stamp (no reveal token,
  // Game Result or winner is invented). Every older marker continues the chain
  // through v26; an older marker carrying v26 evidence receives nothing and is
  // rejected.
  if (fromVersion < 26) {
    const customScripts = (s as { customScripts?: Record<string, Script> }).customScripts ?? {};
    // Phase 9R.1 Astra remediation (Finding A2): local persisted migration
    // uses "trusted" evidence -- this state's OWN saved customScripts,
    // genuine evidence of what the game actually used (unlike remote
    // checkpoint recovery, which never has this and uses "canonical-only"
    // -- see readCheckpoint in storytellerSync.ts and MigrationScriptEvidence's
    // own doc comment).
    // ASTRA-10C-003: each entry migrates from its OWN routed version (see
    // above) -- the same answer checkpoint recovery reaches directly.
    for (const entry of [s.game, ...(isMigratableUndoStack(s.undoStack) ? s.undoStack : [])]) {
      const version = entry && typeof entry === "object" ? entryVersion.get(entry) ?? fromVersion : fromVersion;
      migrateGameEntry(entry, version, { kind: "trusted", customScripts });
    }
  }
  const check = StorytellerStateSchema.safeParse(state);
  if (!check.success) {
    // eslint-disable-next-line no-console
    console.warn("[migrate] persisted state failed validation, resetting:", check.error.flatten());
    _migrationResetFlag = true;
    return CLEAN_STATE;
  }
  // v9 drops obsolete saved previews through the schema allowlist, including
  // undo snapshots. Drafts, sent information and the active session survive.
  Object.assign(s, check.data);
  return state;
}

/** Phase 10H: the role registry of a game's script for the reveal-token
 * seam, memoized per Script object (a custom script edit replaces the object).
 * Null when the script cannot be resolved -- withRevealTokens then compares
 * raw perception inputs (it can only rotate more often, never miss). */
const registryCache = new WeakMap<Script, RoleRegistry>();
const registryForGame = (state: StorytellerStore, game: StorytellerLobbyRecord): RoleRegistry | null => {
  const script = selectScriptById(state, game.scriptId);
  if (!script) return null;
  let registry = registryCache.get(script);
  if (!registry) { registry = buildRegistry(script); registryCache.set(script, registry); }
  return registry;
};

/**
 * Phase 10H: the Game Result a terminal intent declares on `game` (its current
 * live Game Moment), null for End Without Result, or "invalid" for a malformed
 * intent (an unknown kind or winner). Nothing is inferred from game state.
 */
/** PR-10H-004: replacing or discarding the game drops a FAILED terminal
 * intent with it (an in-flight close is never touched here). */
const endedTerminalIntent = (state: { terminalClose: TerminalCloseState | null }): { terminalClose?: null } =>
  state.terminalClose && state.terminalClose.status !== "closing" ? { terminalClose: null } : {};

export function declaredResult(intent: TerminalIntent, game: Pick<StorytellerLobbyRecord, "phase" | "day">): GameResult | null | "invalid" {
  if (!intent || typeof intent !== "object") return "invalid";
  if (intent.kind === "noResult") return null;
  if (intent.kind !== "declare" || (intent.winner !== "good" && intent.winner !== "evil")) return "invalid";
  if (game.phase !== "night" && game.phase !== "day") return "invalid";
  return { winner: intent.winner, declaredAt: { phase: game.phase, day: game.day } };
}

const guardsEqual = (a: GuardStamp | null, b: GuardStamp | null): boolean =>
  a !== null && b !== null && a.token === b.token && a.revision === b.revision;

/** Internal escape hatch: a `set` partial carrying this key bypasses the
 * central localSeq-bumping wrapper below. Never exported — only
 * `restoreRemoteCheckpoint` (the one legitimate "adopt different game
 * content without declaring new local intent" mutation) uses it, and it is
 * stripped before the partial reaches zustand. */
const SKIP_LOCAL_SEQ = Symbol("skipLocalSeq");
/** Phase 10H: marks the one terminal commit (finishGame) that may pass the
 * terminal-close mutation lock. Stripped before the partial reaches zustand. */
const TERMINAL_COMMIT = Symbol("terminalCommit");
type StorytellerPatch =
  | (Partial<StorytellerStore> & { [SKIP_LOCAL_SEQ]?: boolean; [TERMINAL_COMMIT]?: boolean })
  | ((state: StorytellerStore) => Partial<StorytellerStore> & { [SKIP_LOCAL_SEQ]?: boolean; [TERMINAL_COMMIT]?: boolean });

export const useStorytellerStore = create<StorytellerStore>()(
  persist(
    (rawSet, get) => {
      // Central, reviewable game-mutation mechanism (Phase 9C.2A, section 2):
      // every existing action below still calls plain `set(...)` — this
      // shadows that name for the rest of the creator closure, so no
      // individual action needed to change. Any partial that changes `game`
      // to a new, non-null reference bumps `localSeq` exactly once,
      // automatically, with no per-action bookkeeping to forget. Actions
      // that only touch UI-only/local-layout fields (view, selectedPlayerId,
      // grimoireMode, tokenPositions, pendingKnocks, customScripts, lobby,
      // sync, localSeq itself) never include `game` in their partial, so
      // they never bump it. The one deliberate exception — adopting a
      // validated remote checkpoint, which must NOT read as new unsaved
      // Storyteller intent — goes through restoreRemoteCheckpoint, which
      // tags its partial with SKIP_LOCAL_SEQ.
      //
      // The cast back to `typeof rawSet` is the single contained boundary
      // where this wrapper's own permissive parameter type meets zustand's
      // real (overloaded, middleware-augmented) setter type; every action
      // below type-checks against that exact original type, unchanged.
      //
      // Phase 10H (contract §12.2): the same seam maintains every
      // participation's reveal token (withRevealTokens) relative to the
      // Current State being replaced -- so every command AND Undo rotates it
      // on a visible-identity change and mints one for a new participation,
      // with no per-command bookkeeping. An adopted remote checkpoint
      // (SKIP_LOCAL_SEQ) keeps the tokens it carries.
      const set = ((partial: StorytellerPatch, replace?: boolean) => {
        rawSet((state: StorytellerStore) => {
          const resolved = typeof partial === "function" ? partial(state) : partial;
          const { [SKIP_LOCAL_SEQ]: skip, [TERMINAL_COMMIT]: terminal, ...patch } = resolved;
          // Phase 10H (contract §15 step 3, 10H-AC-051): while a terminal close
          // is in flight, gameplay mutation is locked HERE, at the one
          // game-commit seam -- a game or Undo change is dropped (defense in
          // depth behind the locked UI), so the retained ended snapshot is
          // exactly the game the intent was captured on. Only the terminal
          // commit itself passes. A failed close releases the lock.
          if (state.terminalClose?.status === "closing" && !terminal && ("game" in patch || "undoStack" in patch)) {
            delete patch.game;
            delete patch.undoStack;
          }
          if (!skip && "game" in patch && patch.game !== state.game && patch.game != null) {
            const game = withRevealTokens(state.game, patch.game, registryForGame(state, patch.game));
            return { ...patch, game, localSeq: state.localSeq + 1 };
          }
          return patch;
        }, replace as Parameters<typeof rawSet>[1]);
      }) as unknown as typeof rawSet;
      return {
      game: null,
      terminalClose: null,
      view: "home",
      undoStack: [],
      selectedPlayerId: null,
      customScripts: {},
      lobby: null,
      localSeq: 0,
      sync: null,
      pendingKnocks: [],
      grimoireMode: "ring",
      tokenPositions: {},

      newGame: (scriptId: string, opts: NewGameOpts = {}) => {
        // A terminal completion owns the current game's lifecycle until it
        // commits or fails. Refuse at the action boundary before advancing
        // the lifecycle token, clearing UI state, or detaching its lobby.
        if (get().terminalClose?.status === "closing") return;
        const script =
          BUILTIN_SCRIPTS[scriptId] ?? get().customScripts[scriptId];
        if (!script) throw new Error(`Unknown script id: ${scriptId}`);
        gameLifecycle++;
        // Phase 9 Setup finalization (FINAL SETUP INTEGRATION REVISION,
        // Section 4): the store boundary enforces the supported total cap
        // independently of the UI stepper/table, so malformed UI or
        // programmatic input can never create an unsupported plan.
        const count = Math.max(0, Math.min(opts.plannedPlayerCount ?? 0, MAX_TOTAL_PLAYERS));
        const travelerCount = Math.max(0, Math.min(opts.plannedTravelerCount ?? 0, count));
        const prePlayers: Record<PlayerId, STPlayerRecord> = {};
        const preSeatOrder: PlayerId[] = [];
        for (let i = 0; i < count; i++) {
          const id = newId();
          prePlayers[id] = blankPlayer(id, "", i, true);
          preSeatOrder.push(id);
        }
        const game: StorytellerLobbyRecord = {
          gameSchemaVersion: GAME_SCHEMA_VERSION,
          code: "",
          storytellerUid: "local",
          scriptId: script.id,
          phase: "setup",
          day: 0,
          bluffs: [],
          fabled: opts.plannedFabled ?? [],
          lorics: opts.plannedLorics ?? [],
          notes: "",
          players: prePlayers,
          seatOrder: preSeatOrder,
          nightProgress: {},
          rolePool: opts.plannedRoles ?? [],
          plannedPlayerCount: count,
          plannedTravelerCount: travelerCount,
          pendingPlayers: {},
          history: [],
          informationDeliveries: [],
          // Phase 10A: a fresh game observes every Life Event from Night 1.
          lifeEventWindow: freshLifeEventWindow(),
          // Phase 10G: no game-scoped Rule Fact exists before Live Play.
          gameRuleFacts: [],
        };
        usePrivacyStore.getState().reset();
        // PR-10H-003: the previous game's Night cursor (its step keys repeat
        // across games) never reaches this game's Night.
        useShellStore.getState().resetGameScope();
        // PR-10H-004: a failed terminal intent belongs to the game it was
        // declared on and is never retried against a new one.
        set({ game, lobby: null, pendingKnocks: [], view: "game", undoStack: [], selectedPlayerId: null, ...endedTerminalIntent(get()) });
      },

      dealRolePool: () => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const script = selectScriptById(get(), game.scriptId);
        const context = selectSetupContext(game, script);
        const ready = analyzeSetup(context).readiness.deal;
        if (!ready.ok) return ready;
        const pool = game.rolePool ?? [];
        const nonTravelerSeats = context.ordinary.map(p => p.id);

        // Fisher-Yates shuffle
        const shuffled = [...pool];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
        }

        const registry = context.registry!;
        const newPlayers = { ...game.players };
        nonTravelerSeats.forEach((playerId, idx) => {
          const existing = newPlayers[playerId];
          if (!existing) return;
          newPlayers[playerId] = freshAssignment(existing, shuffled[idx]!, registry);
        });

        set({
          undoStack: pushUndo(game, undoStack),
          game: {
            ...game,
            players: newPlayers,
            rolePool: [],
            setupRolesDealt: true,
            // Deal establishes Storyteller truth only; every fresh initial
            // deal requires its own explicit Reveal before publication.
            setupRolesRevealed: false,
          },
        });
        return { ok: true };
      },

      revealRoles: () => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        if (game.phase !== "setup") return { ok: false, message: "This game is no longer in setup." };
        if (!isPostDeal(game)) return { ok: false, message: "Deal the pool before revealing roles." };
        const script = selectScriptById(get(), game.scriptId);
        const context = selectSetupContext(game, script);
        // FINAL POPULATION CLOSURE, Section 11: hard total-capacity bound --
        // even malformed/legacy/adversarial state (bypassing the store-level
        // guards on newGame/setPlannedPlayerCount/addPlayer/etc.) must never
        // reach Reveal as valid.
        if (game.plannedPlayerCount > MAX_TOTAL_PLAYERS || context.population.totalPhysicalSeatCount > MAX_TOTAL_PLAYERS)
          return { ok: false, message: `Starting capacity cannot exceed ${MAX_TOTAL_PLAYERS} participants.` };
        // Reveal requires BOTH identity readiness and setup/composition
        // readiness -- a Manual Override or Edit Bag apply may deliberately
        // leave the setup composition-invalid, and that must still block
        // Reveal even once every shown identity is otherwise complete.
        const analysis = analyzeSetup(context);
        if (!assignedBagIsCoherent(context, analysis))
          return { ok: false, message: "Setup needs correction before revealing roles." };
        const readiness = initialRevealReadiness(context);
        if (!readiness.ready) return { ok: false,
          message: `${readiness.readyCount}/${readiness.totalCount} roles ready to reveal.` };
        // Phase 9R.4 (B8): re-revealing an already-revealed Setup would
        // commit an identical game. Every refusal above is unchanged.
        if (game.setupRolesRevealed === true) return { ok: true };
        set({ undoStack: pushUndo(game, undoStack), game: { ...game, setupRolesRevealed: true } });
        return { ok: true };
      },

      shuffleSetupRoles: () => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const gate = canRefineSetup(game);
        if (!gate.ok) return gate;
        const script = selectScriptById(get(), game.scriptId);
        const context = selectSetupContext(game, script);
        const analysis = analyzeSetup(context);
        if (!assignedBagIsCoherent(context, analysis))
          return { ok: false, message: "Fix the current setup before shuffling roles." };
        const registry = context.registry!;
        const bag = [...context.assigned];
        // Fisher-Yates shuffle -- a fresh private-assignment generation.
        for (let i = bag.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [bag[i], bag[j]] = [bag[j]!, bag[i]!];
        }
        const newPlayers = { ...game.players };
        context.ordinary.forEach((p, idx) => {
          newPlayers[p.id] = freshAssignment(newPlayers[p.id]!, bag[idx]!, registry);
        });
        // Phase 9R.4 (B8): a draw that hands every player back exactly the
        // fresh assignment they already hold changes nothing.
        if (context.ordinary.every((p) => samePlayerApartFromEpoch(game.players[p.id]!, newPlayers[p.id]!)))
          return { ok: true };
        set({ undoStack: pushUndo(game, undoStack), game: { ...game, players: newPlayers } });
        return { ok: true };
      },

      swapSetupRoles: (playerIdA, playerIdB) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const gate = canRefineSetup(game);
        if (!gate.ok) return gate;
        if (playerIdA === playerIdB) return { ok: false, message: "Choose two different players." };
        const a = ownPlayer(game, playerIdA);
        const b = ownPlayer(game, playerIdB);
        if (!a || !b || a.isEmpty || b.isEmpty) return { ok: false, message: "Both players must be seated." };
        if (a.isTraveler || b.isTraveler) return { ok: false, message: "Travelers cannot use Setup Swap." };
        if (!a.actualRole || !b.actualRole) return { ok: false, message: "Both players must have an actual role." };
        const script = selectScriptById(get(), game.scriptId);
        const registry = buildRegistry(script ?? { id: game.scriptId, name: game.scriptId, characters: [] });
        const newA = freshAssignment(a, b.actualRole, registry);
        const newB = freshAssignment(b, a.actualRole, registry);
        // Phase 9R.4 (B8): swapping two equal roles between players already
        // in their fresh assignment changes nothing. A swap that resets
        // either player's deceptive configuration is still a mutation.
        if (samePlayerApartFromEpoch(a, newA) && samePlayerApartFromEpoch(b, newB)) return { ok: true };
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, players: { ...game.players, [playerIdA]: newA, [playerIdB]: newB } },
        });
        return { ok: true };
      },

      replaceSetupRole: (playerId, roleId) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const gate = canRefineSetup(game);
        if (!gate.ok) return gate;
        const player = ownPlayer(game, playerId);
        if (!player || player.isEmpty) return { ok: false, message: "This player is not seated." };
        if (player.isTraveler) return { ok: false, message: "Travelers cannot use the Setup role override." };
        const script = selectScriptById(get(), game.scriptId);
        const role = script?.characters.find(r => r.id === roleId);
        if (!role || !isBagType(role.type))
          return { ok: false, message: "Choose a valid ordinary character for this script." };
        const registry = buildRegistry(script!);
        const next = freshAssignment(player, roleId, registry);
        // Phase 9R.4 (B8): the whole freshAssignment() result decides, not
        // the role alone -- overriding with the current role still resets
        // any deceptive configuration, and only skips when there is none.
        if (samePlayerApartFromEpoch(player, next)) return { ok: true };
        set({ undoStack: pushUndo(game, undoStack), game: { ...game, players: { ...game.players, [playerId]: next } } });
        return { ok: true };
      },

      applyEditedBag: (stagedRoleIds) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const gate = canRefineSetup(game);
        if (!gate.ok) return gate;
        const script = selectScriptById(get(), game.scriptId);
        const context = selectSetupContext(game, script);
        const ordinary = context.ordinary;
        if (stagedRoleIds.length !== ordinary.length) return { ok: false,
          message: `Choose exactly one role per occupied ordinary player (${stagedRoleIds.length}/${ordinary.length}).` };
        const matched = matchBagToAssignments(ordinary.map(p => ({ playerId: p.id, role: p.actualRole })), stagedRoleIds);
        if (!matched) return { ok: false, message: "Setup changed. Review the bag and try again." };
        // Phase 9R.4 (B8): a staged bag whose every occurrence survives
        // reassigns nobody. A `changed` seat always receives a different
        // role (matchBagToAssignments keeps every surviving occurrence), so
        // this is exactly "the result equals Current State".
        if (!matched.some((m) => m.changed)) return { ok: true };
        const registry = context.registry!;
        const newPlayers = { ...game.players };
        for (const { playerId, role, changed } of matched) {
          if (!changed) continue;
          newPlayers[playerId] = freshAssignment(newPlayers[playerId]!, role, registry);
        }
        set({ undoStack: pushUndo(game, undoStack), game: { ...game, players: newPlayers } });
        return { ok: true };
      },

      beginNightOne: () => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const context = selectSetupContext(game, selectScriptById(get(), game.scriptId));
        const analysis = analyzeSetup(context);
        const ready = analysis.readiness.begin;
        if (!ready.ok) return ready;
        // Phase 9 Setup finalization (FINAL SETUP INTEGRATION REVISION,
        // Section 2): defense-in-depth for the committed starting ordinary
        // roster -- a future accidental UI escape must not allow a valid
        // Reveal, an out-of-band mutation away from a valid composition,
        // then beginning Night 1 anyway. Only meaningful once the roster is
        // actually committed; re-uses the same coherence check Reveal itself
        // requires, never a new rule.
        if (isInitialRevealComplete(game) && !assignedBagIsCoherent(context, analysis))
          return { ok: false, message: "The starting ordinary composition is no longer valid. Review Setup before beginning Night 1." };
        // Phase 10A (10A-LUNA-001): the single Setup -> Live Play boundary
        // (setPhase/advancePhase from Setup delegate here). Occupied
        // participants enter Night 1 with canonical starting life state in
        // this SAME commit -- no Life Event, no History, one Undo entry that
        // holds the exact pre-start Setup snapshot.
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, phase: "night", day: 1,
            players: canonicalizeStartingLife(game.players),
            lifeEventWindow: pruneLifeEventWindow(game.lifeEventWindow, "night", 1),
            ...(game.startingNonTravelerCount === undefined && game.day === 0
              ? { startingNonTravelerCount: context.population.occupiedNonTravelerCount } : {}) },
        });
        return { ok: true };
      },

      setPlannedPlayerCount: (count) => {
        const { game, undoStack } = get();
        if (!game || game.phase !== "setup" || !Number.isSafeInteger(count) || count < 1) return;
        // Phase 9 Setup finalization (FINAL SETUP INTEGRATION REVISION,
        // Section 4): the store boundary enforces the supported total cap
        // even when this is called directly, not only through the UI's own
        // stepper/table limits.
        if (count > MAX_TOTAL_PLAYERS) return;
        // FINAL SEAT & TRAVELLER RESERVATION CLOSURE, Section 1: once the
        // starting seat structure exists, plannedPlayerCount is never
        // independently edited -- New Game is the sole initial planner;
        // afterward only Add Seat/Remove Seat/Add Traveller (always in
        // lockstep with physical seats) may change it. Refusing here closes
        // the bypass at the store boundary too, not only by removing the
        // Setup panel's numeric input.
        if (game.seatOrder.length > 0) return;
        if (count === game.plannedPlayerCount) return; // Phase 9R.4 (B8): true no-op
        set({ undoStack: pushUndo(game, undoStack), game: { ...game, plannedPlayerCount: count } });
      },

      setRolePool: (roles) => {
        const { game, undoStack } = get();
        if (!game || game.phase !== "setup") return;
        // Phase 9R.3 (B6): Reveal completes without leaving phase "setup",
        // so the phase check alone let this reopen a committed Setup --
        // wiping the Reveal evidence below. The pool is Setup
        // administration only; later role changes go through assignRole().
        if (isInitialRevealComplete(game)) return;
        // Phase 9R.4 (B8): the pool alone is not the whole result -- a
        // non-empty pool also clears Deal/Reveal evidence. Only when the
        // pool is unchanged AND there is no evidence left to clear is this
        // a true no-op.
        if (sameList(roles, game.rolePool) &&
          (!roles.length || (game.setupRolesDealt === false && game.setupRolesRevealed === false))) return;
        set({ undoStack: pushUndo(game, undoStack), game: { ...game, rolePool: [...roles],
          // Reopening the pool leaves preparation, so any prior deal/reveal
          // evidence for it is no longer meaningful either.
          ...(roles.length ? { setupRolesDealt: false, setupRolesRevealed: false } : {}) } });
      },

      endGame: () => {
        // Do not partially discard a game while its terminal close is in
        // flight. Failed intents remain deliberately discardable below.
        if (get().terminalClose?.status === "closing") return;
        gameLifecycle++;
        usePrivacyStore.getState().reset();
        useShellStore.getState().resetGameScope();
        set({
          ...endedTerminalIntent(get()),
          game: null,
          view: "home",
          undoStack: [],
          selectedPlayerId: null,
          lobby: null,
          pendingKnocks: [],
        });
      },

      finishGame: (intent, recovery = { kind: "fresh" }) => {
        const { game, lobby } = get();
        if (!game) return { ok: false, message: "No game is open." };
        if (game.phase === "ended") return { ok: false, message: "This game is already finished." };
        if (game.phase !== "night" && game.phase !== "day") {
          return { ok: false, message: "Only a game in play (Night or Day) can be finished. Discard a Setup instead." };
        }
        if (lobby) return { ok: false, message: "End the multiplayer lobby first -- the game stays live until its session has closed." };
        const declared = declaredResult(intent, game);
        if (declared === "invalid") return { ok: false, message: "Choose Good victory, Evil victory or End Without Result." };
        // Phase 10H (ASTRA-10H-004): an already-ended session's confirmed
        // outcome wins over this retry's intent -- a declared result AND a
        // confirmed "no result" alike; only a fresh close uses the intent.
        const result = recovery.kind === "endedWithResult" ? recovery.result
          : recovery.kind === "endedWithoutResult" ? null
          : declared;
        // Deliberately NOT setPhase("ended"): that path keeps an Undo entry
        // back into live play. Here the replacement is terminal.
        gameLifecycle++;
        const ended: StorytellerLobbyRecord = { ...game, phase: "ended", lifeEventWindow: pruneLifeEventWindow(game.lifeEventWindow, "ended", game.day) };
        delete ended.result;
        if (result) ended.result = { winner: result.winner, declaredAt: { ...result.declaredAt } };
        set(() => ({
          game: ended,
          undoStack: [],
          lobby: null,
          sync: null,
          pendingKnocks: [],
          selectedPlayerId: null,
          terminalClose: null,
          [TERMINAL_COMMIT]: true,
        }));
        return { ok: true };
      },

      beginTerminalClose: (intent) => {
        const { game, terminalClose } = get();
        if (!game || (game.phase !== "night" && game.phase !== "day")) return false;
        if (terminalClose?.status === "closing") return false;
        if (declaredResult(intent, game) === "invalid") return false;
        set({ terminalClose: { intent, status: "closing", message: null } });
        return true;
      },

      failTerminalClose: (message) => {
        const { terminalClose } = get();
        if (!terminalClose) return;
        set({ terminalClose: { ...terminalClose, status: "failed", message } });
      },

      clearTerminalClose: () => {
        if (get().terminalClose?.status === "closing") return;
        set({ terminalClose: null });
      },

      setView: (view) => set({ view }),

      selectPlayer: (id) => set({ selectedPlayerId: id }),

      setLobby: (lobby) => {
        if (lobby && get().game?.phase === "ended") return false;
        set(state => ({ lobby, game: state.game && lobby ? { ...state.game, code: lobby.code, storytellerUid: lobby.uid } : state.game, undoStack: [] }));
        return true;
      },

      setLobbyStatus: (status) => {
        const { lobby } = get();
        if (!lobby) return;
        set({ lobby: { ...lobby, status } });
      },

      setPendingKnocks: (knocks) => set({ pendingKnocks: knocks }),

      seatPlayerFromKnock: (uid, name) => {
        // Legacy path: used when there are no pre-allocated empty seats.
        // Routes to pending queue; returns null since no seat is assigned yet.
        get().addToPendingQueue(uid, name);
        return null;
      },

      bindRosterUid: (_uid, _playerId) => {
        // No-op in local store. The actual roster→playerId binding lives
        // in the Firebase write done by the sync engine.
      },

      addToPendingQueue: (uid, name) => {
        const { game } = get();
        // ASTRA-10G-002: the waiting queue is closed once the game has ended
        // (membership and seat structure are part of the frozen snapshot).
        if (!game || game.phase === "ended") return;
        const trimmed = name.trim().slice(0, 20);
        if (!trimmed) return;
        if (game.pendingPlayers[uid]) return; // already queued
        set({
          game: {
            ...game,
            pendingPlayers: { ...game.pendingPlayers, [uid]: trimmed },
          },
        });
      },

      assignPendingToSeat: (uid, seatPlayerId, participantId) => {
        const { game } = get();
        // ASTRA-10G-002: never seats anyone into an ended game -- however the
        // assignment arrived (a stale popup, an assignment begun before
        // Finish game). Refused before any mutation, so a membership seating
        // command sees false and rolls its remote binding back.
        if (!game || game.phase === "ended") return false;
        const name = game.pendingPlayers[uid];
        if (!name) return false;
        const seat = ownPlayer(game, seatPlayerId);
        if (!seat?.isEmpty) return false;
        // Phase 9R.3 (B7): refused before any mutation, so the membership
        // seating command (seatPlayerAndCommit) sees false and revokes the
        // remote binding it just wrote.
        if (atOccupancyCapacity(game)) return false;
        // Phase 9R.2 (Astra R1): a supplied identity must be a brand-new
        // participation instance -- never one this game has already used
        // (current or historical). Freshly minted ids always pass.
        if (participantId !== undefined &&
          (typeof participantId !== "string" || !participantId || participantIdAppearsIn(game, participantId))) return false;
        const newPending = { ...game.pendingPlayers };
        delete newPending[uid];
        set({
          // Membership transitions establish a new remote-consistency
          // boundary; older snapshots must not resurrect a stale seat.
          undoStack: [],
          game: {
            ...game,
            players: {
              ...game.players,
              [seatPlayerId]: occupySeat(seat, name, game, participantId),
            },
            pendingPlayers: newPending,
          },
        });
        return true;
      },

      restoreSeatedMember: (uid, seatPlayerId, name, participantId) => {
        const { game, selectedPlayerId } = get();
        if (!game || !Object.prototype.hasOwnProperty.call(game.players, seatPlayerId)) return false;
        const seat = game.players[seatPlayerId];
        if (!seat?.isEmpty) return false;
        const trimmed = typeof name === "string" ? name.trim().slice(0, 20) : "";
        if (!trimmed || typeof participantId !== "string" || !participantId) return false;
        // The authoritative record names ONE live participation instance; it
        // can never be seated twice.
        if (Object.values(game.players).some((p) => p.participantId === participantId)) return false;
        const newPending = { ...game.pendingPlayers };
        delete newPending[uid];
        set({
          // A membership transition, exactly like assignPendingToSeat: older
          // snapshots must not resurrect a stale seat.
          undoStack: [],
          game: {
            ...game,
            players: { ...game.players, [seatPlayerId]: occupySeat(seat, trimmed, game, participantId) },
            pendingPlayers: newPending,
          },
          selectedPlayerId: selectedPlayerId === seatPlayerId ? null : selectedPlayerId,
        });
        return true;
      },

      removePendingPlayer: (uid) => {
        const { game } = get();
        // Phase 9R.4 (B8): removing a uid that is not queued changes nothing
        // -- it must not replace the game or advance localSeq.
        // ASTRA-10G-002: an ended game's queue is frozen too.
        if (!game || game.phase === "ended" || !Object.prototype.hasOwnProperty.call(game.pendingPlayers, uid)) return;
        const newPending = { ...game.pendingPlayers };
        delete newPending[uid];
        set({ game: { ...game, pendingPlayers: newPending } });
      },

      addCustomScript: (script) => {
        if (BUILTIN_SCRIPT_IDS.has(script.id)) {
          return {
            ok: false,
            error: `Script id "${script.id}" conflicts with a built-in script. Rename and re-import.`,
          };
        }
        const { customScripts } = get();
        if (customScripts[script.id]) {
          return {
            ok: false,
            error: `A custom script with id "${script.id}" is already imported. Remove it first to replace.`,
          };
        }
        set({
          customScripts: { ...customScripts, [script.id]: script },
        });
        return { ok: true };
      },

      removeCustomScript: (id) => {
        const { customScripts, game } = get();
        if (!customScripts[id]) return;
        const next = { ...customScripts };
        delete next[id];
        // If the active game uses this script, end it cleanly.
        if (game && game.scriptId === id) {
          set({
            customScripts: next,
            game: null,
            view: "home",
            undoStack: [],
            selectedPlayerId: null,
          });
        } else {
          set({ customScripts: next });
        }
      },

      addPlayer: (name) => {
        const { game, undoStack } = get();
        if (!game) return;
        const trimmed = name.trim();
        if (!trimmed) return;
        // FINAL POPULATION CLOSURE / RESERVATION CLOSURE, Section 2: refuse
        // atomically -- before any mutation -- once either the plan or
        // physical seats are already at capacity. Never clamp the plan
        // while still creating seat/player 21; state must remain
        // completely unchanged on refusal.
        if (atCapacity(game)) return;
        const id = newId();
        const seat = game.seatOrder.length;
        const player = occupySeat(blankPlayer(id, "", seat, true), trimmed, game);
        // Phase 9 Setup finalization (FINAL SETUP INTEGRATION REVISION,
        // Section 3.D; FINAL POPULATION CLOSURE, Section 6): addPlayer
        // always creates a brand-new seat rather than filling an existing
        // planned one, so a deliberate addition before Reveal genuinely
        // grows the plan -- never derived from occupancy. Arrival type is
        // decided first (via arrivalPlayer, above) and the plan update
        // matches it atomically in this same patch: a genuinely new
        // Traveler arrival (e.g. ordinary occupancy already at its cap)
        // must never leave plannedTravelerCount silently behind
        // plannedPlayerCount. Once the starting roster is committed
        // (Reveal), the plan is historical and no longer grows; late
        // arrivals stay a pure occupancy/geometry change.
        const planPatch = isInitialRevealComplete(game) ? {} : {
          plannedPlayerCount: game.plannedPlayerCount + 1,
          ...(player.isTraveler ? { plannedTravelerCount: game.plannedTravelerCount + 1 } : {}),
        };
        set({
          undoStack: pushUndo(game, undoStack),
          game: {
            ...game,
            ...planPatch,
            players: { ...game.players, [id]: player },
            seatOrder: [...game.seatOrder, id],
          },
        });
      },

      addPlayerToSeat: (name) => {
        const { game } = get();
        if (!game) return;
        const trimmed = name.trim();
        if (!trimmed) return;
        const emptyId = game.seatOrder.find((id) => game.players[id]?.isEmpty);
        if (!emptyId) {
          get().addPlayer(trimmed);
          return;
        }
        const seat = game.players[emptyId];
        if (!seat) return;
        // Phase 9R.3 (B7): see atOccupancyCapacity.
        if (atOccupancyCapacity(game)) return;
        set({
          undoStack: pushUndo(game, get().undoStack),
          game: {
            ...game,
            players: {
              ...game.players,
              [emptyId]: occupySeat(seat, trimmed.slice(0, 20), game),
            },
          },
        });
      },

      addEmptySeat: () => {
        const { game } = get();
        if (!game) return;
        // FINAL POPULATION CLOSURE / RESERVATION CLOSURE, Sections 2 & 7:
        // this represents deliberate new starting capacity before Reveal, so
        // it grows the plan exactly like addPlayer -- refused atomically
        // once either the plan or physical seats are at capacity, never
        // clamped while still creating the seat. Filling this seat later
        // (addPlayerToSeat/assignPendingToSeat) never grows the plan again;
        // it is already accounted for here.
        if (atCapacity(game)) return;
        const id = newId();
        const seat = game.seatOrder.length;
        const revealed = isInitialRevealComplete(game);
        // FINAL SEAT & TRAVELLER RESERVATION CLOSURE, Section 3: before
        // Reveal, once the ordinary target is already at its own cap (15), a
        // new generic seat cannot be an ordinary reservation -- the new
        // participant becomes planned Traveller capacity instead, exactly
        // like Add Traveller, so the ordinary target itself never silently
        // exceeds 15. The seat stays isTraveler: false, marked only
        // plannedTravelerSeat -- actual Traveller identity occurs once a
        // real player occupies it.
        const target = revealed ? null : selectSetupContext(game).population.targetNonTravelerCount;
        const atOrdinaryCap = target !== null && target >= MAX_PLAYERS;
        const planPatch = revealed ? {} : atOrdinaryCap
          ? { plannedPlayerCount: game.plannedPlayerCount + 1, plannedTravelerCount: game.plannedTravelerCount + 1 }
          : { plannedPlayerCount: game.plannedPlayerCount + 1 };
        const newPlayer = atOrdinaryCap
          ? { ...blankPlayer(id, "", seat, true), plannedTravelerSeat: true }
          : arrivalPlayer(blankPlayer(id, "", seat, true), game);
        set({
          undoStack: pushUndo(game, get().undoStack),
          game: {
            ...game,
            ...planPatch,
            players: { ...game.players, [id]: newPlayer },
            seatOrder: [...game.seatOrder, id],
          },
        });
      },

      addTravelerSeat: () => {
        const { game } = get();
        if (!game) return;
        // FINAL POPULATION CLOSURE / RESERVATION CLOSURE, Section 2: refuse
        // atomically once either the plan or physical seats are at capacity.
        if (atCapacity(game)) return;
        const id = newId();
        const seat = game.seatOrder.length;
        // Phase 9 Setup finalization (FINAL SETUP INTEGRATION REVISION,
        // Section 3.E; FINAL POPULATION CLOSURE, Section 3-4): capacity for
        // an intended Traveler is added directly to the plan, never by
        // pre-flagging an empty seat isTraveler -- that would treat the
        // empty seat itself as the Traveler player. The seat instead
        // carries plannedTravelerSeat, narrow Storyteller-only planning
        // metadata representing unfulfilled planned Traveler capacity.
        // Actual designation happens through arrivalPlayer() the moment a
        // real player occupies it (see assignPendingToSeat/addPlayerToSeat),
        // which fulfills and clears this exact reservation -- correctly
        // never double-counted against plannedTravelerCount.
        const planPatch = isInitialRevealComplete(game) ? {} : {
          plannedPlayerCount: game.plannedPlayerCount + 1,
          plannedTravelerCount: game.plannedTravelerCount + 1,
        };
        set({
          undoStack: pushUndo(game, get().undoStack),
          game: {
            ...game,
            ...planPatch,
            players: { ...game.players, [id]: { ...blankPlayer(id, "", seat, true), plannedTravelerSeat: true } },
            seatOrder: [...game.seatOrder, id],
          },
        });
      },

      removePlayer: (id) => {
        const { game, selectedPlayerId } = get();
        const existing = game ? ownPlayer(game, id) : undefined;
        if (!game || !existing) return false;
        const seatOrder = game.seatOrder.filter((p) => p !== id);
        // re-seat the remaining players to keep seats contiguous 0..n-1,
        // and scrub any fakeMinion reference to the removed player.
        // Phase 9R.2: removal ends the removed person's participation
        // instance along with the seat. History, Provenance, Information
        // Delivery, and Effect/Reminder source snapshots that name them are
        // deliberately left untouched -- they are durable ParticipantRefs,
        // not pointers into this roster (fakeMinions, by contrast, is a
        // live "who is here now" draft and is scrubbed).
        //
        // Phase 9R.5: this deletes exactly `id` and nothing else. The
        // surviving map starts from every OWN record, not from seatOrder
        // alone -- rebuilding it solely by walking seatOrder silently
        // deleted any own record a malformed order had already omitted.
        // Seated survivors are renumbered (in seat order, as before); an
        // unseated one is carried over with its geometry untouched, never
        // guessed -- keeping such state from becoming authoritative is the
        // persisted-game validator's job (schemas.ts checkSeatGeometry).
        const survivors = new Map<PlayerId, STPlayerRecord>();
        seatOrder.forEach((pid, idx) => {
          const p = ownPlayer(game, pid);
          if (p) survivors.set(pid, { ...p, seat: idx });
        });
        for (const [pid, p] of Object.entries(game.players)) {
          if (pid !== id && !survivors.has(pid)) survivors.set(pid, p);
        }
        const renumbered = Object.fromEntries([...survivors].map(([pid, p]): [PlayerId, STPlayerRecord] => {
          if (!p.privateInfo?.fakeMinions?.includes(id)) return [pid, p];
          const next = { ...p };
          const filtered = p.privateInfo.fakeMinions.filter(
            (mid) => mid !== id
          );
          const pi = { ...p.privateInfo };
          if (filtered.length > 0) pi.fakeMinions = filtered;
          else delete pi.fakeMinions;
          if (Object.keys(pi).length > 0) next.privateInfo = pi;
          else delete next.privateInfo;
          return [pid, next];
        }));
        // Phase 9 Setup finalization (FINAL POPULATION CLOSURE, Section 8):
        // removing a seat before Reveal always removes one unit of starting
        // capacity -- symmetric with addEmptySeat/addTravelerSeat/addPlayer
        // always having grown it, whether or not the seat was ever filled.
        // unseatPlayer -- not this command -- is the "keep the reservation"
        // action that must never touch the plan (Section 9). Exactly one
        // Traveler-planning decrement applies, never both: either this seat
        // still carried an unfulfilled Traveler reservation (empty,
        // plannedTravelerSeat), or it held an actual occupied Traveler --
        // arrivalPlayer() guarantees those two are mutually exclusive, so
        // there is never a double-decrement here.
        const adjustPlan = !isInitialRevealComplete(game);
        const planPatch = adjustPlan ? {
          plannedPlayerCount: Math.max(0, game.plannedPlayerCount - 1),
          ...(existing.plannedTravelerSeat || existing.isTraveler
            ? { plannedTravelerCount: Math.max(0, game.plannedTravelerCount - 1) } : {}),
        } : {};
        set({
          // Membership transitions establish a new remote-consistency
          // boundary; older snapshots must not resurrect a stale seat.
          undoStack: [],
          game: {
            ...game,
            ...planPatch,
            players: renumbered,
            seatOrder,
          },
          selectedPlayerId: selectedPlayerId === id ? null : selectedPlayerId,
        });
        return true;
      },

      unseatPlayer: (id) => {
        const { game, selectedPlayerId } = get();
        const existing = game ? ownPlayer(game, id) : undefined;
        if (!game || !existing || existing.isEmpty) return false;
        // FINAL SEAT & TRAVELLER RESERVATION CLOSURE, Section 4: unseating
        // changes occupancy only, never the seat's reservation type. An
        // occupied Traveler returns to an empty Traveller reservation
        // (plannedTravelerSeat: true), not a generic ordinary-neutral blank
        // seat -- otherwise refilling it would silently lose the Traveller
        // designation. Plan counts (plannedPlayerCount/plannedTravelerCount)
        // never change here; only reservation type does.
        //
        // Phase 9R.2: this ENDS the departing person's participation
        // instance. The seat/PlayerId survives; the rebuilt blank seat
        // deliberately carries no `participantId`, so whoever fills it
        // next gets a fresh one (occupySeat). Historical ParticipantRefs
        // already stored elsewhere keep naming the departed person.
        const blank = blankPlayer(id, "", existing.seat, true);
        const restored = existing.isTraveler ? { ...blank, plannedTravelerSeat: true } : blank;
        set({
          // Membership-affecting changes deliberately do not enter the generic
          // undo stack; clearing older snapshots prevents undo from restoring
          // a remote membership that has already been revoked.
          undoStack: [],
          game: {
            ...game,
            players: {
              ...game.players,
              [id]: restored,
            },
          },
          selectedPlayerId: selectedPlayerId === id ? null : selectedPlayerId,
        });
        return true;
      },

      renamePlayer: (id, name) => {
        const { game, undoStack } = get();
        if (!game) return;
        const trimmed = name.trim().slice(0, 20);
        if (!trimmed) return;
        // Phase 9R.4 (B8): an unknown player (patchPlayer would return the
        // same game while Undo still grew) or the already-stored normalized
        // name is a true no-op.
        const existing = ownPlayer(game, id);
        if (!existing || existing.name === trimmed) return;
        set({
          undoStack: pushUndo(game, undoStack),
          game: patchPlayer(game, id, { name: trimmed }),
        });
      },

      setSeatOrder: (order) => {
        const { game, undoStack } = get();
        if (!game) return;
        // Phase 9R.4 (B8 remediation #2): anything but an exact permutation
        // of the current seats (unknown or inherited ids, duplicates,
        // omissions, extra slots) is an invalid request -- rejected before
        // renumbering, Undo, or set().
        if (!isSeatPermutation(game, order)) return;
        // Phase 9R.4 (B8): resubmitting the current order, with every seat
        // already numbered to match it, changes nothing.
        if (sameList(order, game.seatOrder) &&
          order.every((pid, idx) => { const p = ownPlayer(game, pid); return !p || p.seat === idx; })) return;
        const renumbered = { ...game.players };
        order.forEach((pid, idx) => {
          const p = ownPlayer({ players: renumbered }, pid);
          if (p) renumbered[pid] = { ...p, seat: idx };
        });
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, players: renumbered, seatOrder: [...order] },
        });
      },

      movePlayer: (id, direction) => {
        const { game, undoStack } = get();
        if (!game) return;
        // Phase 9R.4 (Astra A): the target must be a real own player before
        // seatOrder is consulted -- a recovered seatOrder may carry a string
        // with no player record behind it, and moving it would reorder
        // authoritative seats for nobody.
        if (!ownPlayer(game, id)) return;
        const order = [...game.seatOrder];
        const i = order.indexOf(id);
        if (i < 0) return;
        const j = direction === "left" ? i - 1 : i + 1;
        if (j < 0 || j >= order.length) return;
        [order[i], order[j]] = [order[j]!, order[i]!];
        const renumbered = { ...game.players };
        order.forEach((pid, idx) => {
          const p = ownPlayer({ players: renumbered }, pid);
          if (p) renumbered[pid] = { ...p, seat: idx };
        });
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, players: renumbered, seatOrder: order },
        });
      },

      resolveRoles: (transaction) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        // guard -> plan (pure) -> one commit. The planner binds every target
        // to the participation instance and the observed state the caller
        // saw, validates each intent in order against the evolving working
        // state, and returns a refusal, a true no-op, or the partial-field
        // Role plan (patches + History).
        const result = planRoleTransaction(game, transaction, { script: selectScriptById(get(), game.scriptId), ids: defaultRoleIds });
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false };
        set({ undoStack: pushUndo(game, undoStack), game: applyRolePlan(game, result.plan) });
        return { ok: true, changed: true };
      },

      resolveGameRuleFacts: (transaction) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        // guard -> plan (pure) -> persistence preflight -> one commit. An ended
        // game, Setup, an unregistered type, a malformed request or an unsafe
        // result is refused with NO change to the game, History, Undo or
        // localSeq; a true no-op changes nothing either.
        const result = planGameRuleFactTransaction(game, transaction);
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false };
        const planned = applyGameRuleFactPlan(game, result.plan);
        // PHASE10G Section 4.5: a NEW authoritative mutation -- the persisted
        // game/schema validation, then the Firebase-writable and recovery-
        // checkpoint-envelope proofs, all before any replacement.
        if (!StorytellerGamePersistedSchema.safeParse(planned).success) {
          return { ok: false, code: "invalid", message: "The resulting game failed validation -- nothing was recorded." };
        }
        const unsafe = persistencePreflight(planned, game, get().lobby);
        if (unsafe) return { ok: false, code: "invalid", message: unsafe };
        set({ undoStack: pushUndo(game, undoStack), game: planned });
        return { ok: true, changed: true };
      },

      resolveAbility: (request, semantics) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        const script = selectScriptById(get(), game.scriptId) ?? null;
        // plan (pure, against Current State as it is NOW) -> one commit.
        const result = planAbilityResolution(game, request, {
          script,
          registry: buildRegistry(script ?? { id: game.scriptId, name: "", characters: [] }),
          semantics: semantics ?? CANONICAL_ABILITY_SEMANTICS,
        });
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false };
        // SOL-10F-C3: an accepted plan becomes authoritative only if the
        // production writer can project it. The SAME compatibility check the
        // checkpoint recovery uses, against the SAME Storyteller destination
        // the writer writes -- the live lobby's, or (before a room exists) the
        // canonical maximum supported code shape. Refused -> nothing committed
        // (no game, Undo, localSeq, History, delivery or Night progress); the
        // pure coordinator stays storage-agnostic.
        const writable = validateFirebaseWritableValue(result.plan.game, storytellerPathSegments(get().lobby?.code ?? MAX_ROOM_CODE_SHAPE));
        if (!writable.ok) {
          return { ok: false, code: "invalidComposition",
            message: `This result cannot be safely stored online (the resulting game ${writable.message.replace(/\.$/, "")}) -- nothing was recorded. Resolve it another way.` };
        }
        // SOL-10F-E1: the writer also persists the whole game as ONE derived
        // checkpoint string beside the live roster this store does not own.
        // Proven against the conservative supported-roster envelope (every
        // seat bound to a maximum-length UID, under any room) with the SAME
        // serializer and validator the writer uses -- never an empty roster.
        const checkpoint = validateCheckpointEnvelope(result.plan.game, game);
        if (!checkpoint.ok) {
          return { ok: false, code: "invalidComposition",
            message: `This result cannot be safely stored online (the game's recovery checkpoint ${checkpoint.message.replace(/\.$/, "")}) -- nothing was recorded. Resolve it another way.` };
        }
        set({ undoStack: pushUndo(game, undoStack), game: result.plan.game });
        return { ok: true, changed: true, resolutionId: result.plan.resolutionId };
      },

      assignRole: (id, roleId, context) => {
        const player = currentOccupant(get().game, id);
        if (!player) return notSeatedRefusal();
        // Compatibility meaning: this adapter never flips ordinary-vs-Traveler
        // status (an ordinary player is not given a Traveler character here,
        // nor a Traveler an ordinary one).
        if (typeof roleId === "string" && roleId && !!getTraveler(roleId) !== player.isTraveler) {
          return { ok: false, code: "role", message: "That character does not fit this player's Traveler status." };
        }
        return get().resolveRoles({ intents: [changeRoleIntent(player, roleId)], ...(context ? { context } : {}) });
      },

      correctRole: (id, roleId, options, context) => {
        const player = currentOccupant(get().game, id);
        if (!player) return notSeatedRefusal();
        if (typeof roleId === "string" && roleId && !!getTraveler(roleId) !== player.isTraveler) {
          return { ok: false, code: "role", message: "That character does not fit this player's Traveler status." };
        }
        return get().resolveRoles({ intents: [correctRoleIntent(player, roleId, options?.travelerArrivalPolicy)],
          ...(context ? { context } : {}) });
      },

      showAssignedRole: (id) => {
        const player = currentOccupant(get().game, id);
        if (!player?.actualRole || needsShownIdentity(player.actualRole)) return { ok: true, changed: false };
        return get().setShownRole(id, player.actualRole);
      },

      setShownRole: (id, roleId) => {
        const player = currentOccupant(get().game, id);
        if (!player) return notSeatedRefusal();
        // A different Shown Role derives its alignment (null); the very same
        // Shown Role keeps the alignment as it is (never a silent clear).
        return get().setPerception(id, {
          shownRole: roleId,
          shownAlignment: roleId !== null && roleId === player.shownRole ? player.shownAlignment : null,
        });
      },

      setShownAlignment: (id, alignment) => {
        const player = currentOccupant(get().game, id);
        if (!player) return notSeatedRefusal();
        return get().setPerception(id, { shownRole: player.shownRole, shownAlignment: alignment });
      },

      setBehaviorMode: (id, mode) => {
        const player = currentOccupant(get().game, id);
        if (!player) return notSeatedRefusal();
        return get().setPerception(id, { shownRole: player.shownRole, shownAlignment: player.shownAlignment, behaviorMode: mode });
      },

      setPerception: (id, perception, context) => {
        const player = currentOccupant(get().game, id);
        if (!player) return notSeatedRefusal();
        return get().resolveRoles({ intents: [setPerceptionIntent(player, perception)], ...(context ? { context } : {}) });
      },

      setBluffs: (id, bluffs) => {
        const { game, undoStack } = get();
        if (!game) return;
        const player = ownPlayer(game, id);
        if (!player) return;
        const next = { ...player };
        const cleaned = bluffs.filter((b) => !!b).slice(0, 3);
        if (cleaned.length === 0) {
          // Drop the bluffs key entirely; if privateInfo becomes empty, drop it too.
          if (next.privateInfo) {
            const { bluffs: _drop, ...rest } = next.privateInfo;
            const remaining = Object.keys(rest).length > 0 ? rest : undefined;
            if (remaining) next.privateInfo = remaining;
            else delete next.privateInfo;
          }
        } else {
          next.privateInfo = { ...(next.privateInfo ?? {}), bluffs: cleaned };
        }
        // Phase 9R.4 (B8): compare the canonicalized stored result, never
        // the raw input. Order stays significant, exactly as stored.
        if (sameSnapshot(next.privateInfo, player.privateInfo)) return;
        set({
          undoStack: pushUndo(game, undoStack),
          game: {
            ...game,
            players: { ...game.players, [id]: next },
          },
        });
      },

      setFakeMinions: (id, playerIds) => {
        const { game, undoStack } = get();
        if (!game) return;
        const player = ownPlayer(game, id);
        if (!player) return;
        const valid = [...new Set(playerIds.filter((pid) => { const q = ownPlayer(game, pid); return !!q && !q.isEmpty && pid !== id; }))];
        const next = { ...player };
        if (valid.length === 0) {
          if (next.privateInfo) {
            const { fakeMinions: _drop, ...rest } = next.privateInfo;
            const remaining = Object.keys(rest).length > 0 ? rest : undefined;
            if (remaining) next.privateInfo = remaining;
            else delete next.privateInfo;
          }
        } else {
          next.privateInfo = {
            ...(next.privateInfo ?? {}),
            fakeMinions: valid,
          };
        }
        // Phase 9R.4 (B8): see setBluffs -- filtered/deduped input that
        // canonicalizes to the stored selection is a true no-op.
        if (sameSnapshot(next.privateInfo, player.privateInfo)) return;
        set({
          undoStack: pushUndo(game, undoStack),
          game: {
            ...game,
            players: { ...game.players, [id]: next },
          },
        });
      },

      setPrivateText: (id, text) => {
        const { game, undoStack } = get();
        const player = game ? ownPlayer(game, id) : undefined;
        if (!game || !player) return;
        // Phase 9R.4 (B8): compare the normalized stored extraText (blank ->
        // absent, 4000-character truncation). An absent privateInfo and an
        // empty one read identically everywhere (commitExtraTextDraft
        // already treats them as clean), so clearing absent text is inert.
        if ((text.trim() ? text.slice(0, 4000) : undefined) === player.privateInfo?.extraText) return;
        const privateInfo = { ...player.privateInfo };
        if (text.trim()) privateInfo.extraText = text.slice(0, 4000);
        else delete privateInfo.extraText;
        set({
          undoStack: pushUndo(game, undoStack),
          game: patchPlayer(game, id, { privateInfo }),
        });
      },

      setIsTraveler: (id, isTraveler) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const existing = ownPlayer(game, id);
        if (!existing) return { ok: false, message: "This player is not seated." };
        // SOL-10E-A1 (ASTRA-10E-001): Setup-only ownership. This Setup
        // writer (which also resets Role, perception and Actual Alignment)
        // runs only while the game itself is still in Setup -- Night, Day and
        // Ended (even an ended pre-Reveal snapshot) refuse before any
        // mutation. Live ordinary <-> Traveler transitions belong to the
        // Phase 10D Role seam, never to this command.
        if (game.phase !== "setup") return { ok: false, message: "Traveler status is set up only during Setup." };
        if (existing.isTraveler === isTraveler) return { ok: true };
        // Phase 9 Setup finalization (FINAL SETUP INTEGRATION REVISION,
        // Section 2): Reveal is a hard starting-setup commitment boundary --
        // the starting ordinary roster is committed, so ordinary <-> Traveler
        // conversion is refused at this command boundary, not only hidden
        // in the UI.
        if (isInitialRevealComplete(game))
          return { ok: false, message: "Roles are already revealed; Traveler status is locked in for this game." };
        // Section 3.B: only an occupied player can be designated a Traveler
        // -- an empty seat is never itself a Traveler player.
        if (existing.isEmpty) return { ok: false, message: "Seat a player before designating them a Traveler." };
        const population = selectSetupContext(game).population;
        const occupiedOrdinary = population.occupiedNonTravelerCount;
        const occupiedTravelers = population.occupiedTravelerCount;
        const nextOccupiedOrdinary = isTraveler ? occupiedOrdinary - 1 : occupiedOrdinary + 1;
        if (isTraveler && nextOccupiedOrdinary < MIN_PLAYERS)
          return { ok: false, message: `Converting this player to a Traveler would drop ordinary players below the minimum of ${MIN_PLAYERS}.` };
        if (!isTraveler && nextOccupiedOrdinary > MAX_PLAYERS)
          return { ok: false, message: `Converting this Traveler to ordinary would raise ordinary players above the maximum of ${MAX_PLAYERS}.` };
        // Section 3.B/3.A: plan and occupancy are never conflated. A
        // designation either fulfills an already-planned Traveler slot
        // (occupied Travelers have not yet reached the planned count) or
        // represents a genuinely new intended Traveler -- never derived by
        // snapping to current occupancy. The reverse direction simply
        // relinquishes one intended Traveler slot from the plan.
        // FINAL SEAT & TRAVELLER RESERVATION CLOSURE, Section 5: Traveller
        // capacity already spoken for is occupied Travelers PLUS empty
        // seats still holding an outstanding Traveller reservation
        // (plannedTravelerSeat) -- counting occupied Travelers alone would
        // let a manual designation silently "steal" a reservation meant for
        // a different, still-unfilled seat instead of growing the plan for
        // itself, undercounting total planned Travellers once the
        // reservation is later filled too.
        const allocatedTravelerCapacity = occupiedTravelers + population.outstandingTravelerReservationCount;
        const nextPlannedTravelerCount = isTraveler
          ? (allocatedTravelerCapacity < game.plannedTravelerCount ? game.plannedTravelerCount : game.plannedTravelerCount + 1)
          : Math.max(0, game.plannedTravelerCount - 1);
        const next: STPlayerRecord = {
          ...existing,
          isTraveler,
          actualRole: "",
          shownRole: null,
          shownAlignment: null,
          behaviorMode: "normal",
          publicDisplayRole: null,
        };
        delete next.actualAlignment;
        delete next.exiled;
        delete next.travelerArrival;
        if (isTraveler) next.travelerArrival = newTravelerArrival();
        delete next.privateInfo;
        set({
          undoStack: pushUndo(game, undoStack),
          game: {
            ...game,
            plannedTravelerCount: nextPlannedTravelerCount,
            nightProgress: resetTravelerNightProgress(game, id),
            players: { ...game.players, [id]: invalidatePrivatePacket(next) },
          },
        });
        return { ok: true };
      },

      resolveAlignments: (transaction) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        // guard -> plan (pure) -> one commit. The planner binds every target
        // to the participation instance and the Actual Alignment / Traveler
        // status the caller saw, validates every intent before anything is
        // committed, and returns a refusal, a true no-op, or the partial-field
        // Alignment plan (patches + History).
        const result = planAlignmentTransaction(game, transaction, { ids: defaultAlignmentIds });
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false };
        set({ undoStack: pushUndo(game, undoStack), game: applyAlignmentPlan(game, result.plan) });
        return { ok: true, changed: true };
      },

      setTravelerAlignment: (id, alignment, context) => {
        const player = currentOccupant(get().game, id);
        if (!player) return alignmentNotSeated();
        // Compatibility meaning: Traveler-only (the seam itself serves every
        // participant).
        if (!player.isTraveler) return { ok: false, code: "invalid", message: "This player is not a Traveler." };
        return get().resolveAlignments({ intents: [changeAlignmentIntent(player, alignment)], ...(context ? { context } : {}) });
      },

      setActualAlignment: (id, alignment, context) => {
        const player = currentOccupant(get().game, id);
        if (!player) return alignmentNotSeated();
        return get().resolveAlignments({ intents: [changeAlignmentIntent(player, alignment)], ...(context ? { context } : {}) });
      },

      prepareTravelerDemon: (id) => {
        const { game, undoStack } = get();
        const p = game ? ownPlayer(game, id) : undefined;
        const script = game && selectScriptById(get(), game.scriptId);
        if (!game || !p || !script) return;
        const result = travelerDemonInformation(p, game, buildRegistry(script));
        if (!result.demon) return;
        const privateInfo = { travelerDemon: result.demon.id };
        // Phase 9R.4 (B8): the same prepared Demon is already stored.
        if (sameSnapshot(p.privateInfo, privateInfo)) return;
        set({ undoStack: pushUndo(game, undoStack), game: patchPlayer(game, id, { privateInfo }) });
      },

      completeTravelerInformation: (id) => {
        const { game, undoStack } = get();
        const p = game ? ownPlayer(game, id) : undefined;
        if (!game || !p || !publicTravelerRole(p) || p.actualAlignment !== "evil" || !p.alive || p.exiled) return;
        if (p.travelerArrival?.demonInfoComplete === true) return; // Phase 9R.4 (B8): already complete
        set({ undoStack: pushUndo(game, undoStack), game: patchPlayer(game, id,
          { travelerArrival: { ...(p.travelerArrival ?? newTravelerArrival()), demonInfoComplete: true } }) });
      },

      // Phase 10A: exile is its own Life Event kind (never collapsed into a
      // generic death) recorded through the life-resolution boundary.
      exileTraveler: (id) => { get().recordExile(id, "died"); },

      completeTravelerArrivalCheck: (id) => {
        const { game, undoStack } = get();
        const p = game ? ownPlayer(game, id) : undefined;
        if (!game || !p || !travelerNeedsArrivalCheck(p) || !p.actualAlignment || !p.alive || p.exiled) return;
        if (p.travelerArrival?.arrivalCheckComplete === true) return; // Phase 9R.4 (B8): already complete
        set({ undoStack: pushUndo(game, undoStack), game: patchPlayer(game, id,
          { travelerArrival: { ...(p.travelerArrival ?? newTravelerArrival()), arrivalCheckComplete: true } }) });
      },

      setFabled: (fabled) => {
        const { game, undoStack } = get();
        if (!game) return;
        const validIds = new Set(FABLED.map((f) => f.id));
        const deduped = [...new Set(fabled.filter((id) => validIds.has(id)))];
        if (sameList(deduped, game.fabled)) return; // Phase 9R.4 (B8): same normalized collection
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, fabled: deduped },
        });
      },

      setLorics: (lorics) => {
        const { game, undoStack } = get();
        if (!game) return;
        const validIds = new Set(LORICS.map((l) => l.id));
        const deduped = [...new Set(lorics.filter((id) => validIds.has(id)))];
        if (sameList(deduped, game.lorics)) return; // Phase 9R.4 (B8): same normalized collection
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, lorics: deduped },
        });
      },

      resolveLife: (transaction) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "refused", message: "No game is open." };
        // guard -> plan (pure) -> one commit. The planner validates every
        // intent against Current State and returns a refusal, a true no-op,
        // or the complete Current State + Life Event Window + History plan.
        const result = planLifeTransaction(game, transaction);
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false, eventIds: [] };
        set({ undoStack: pushUndo(game, undoStack), game: applyLifePlan(game, result.plan) });
        return { ok: true, changed: true, eventIds: result.plan.addedEventIds };
      },

      recordDeath: (id, context) =>
        get().resolveLife({ intents: [{ kind: "death", playerId: id }], context }),

      recordExecution: (id, outcome, options, context) =>
        get().resolveLife({ intents: [{ kind: "execution", playerId: id, outcome,
          ...(options?.confirmations?.length ? { confirmations: options.confirmations } : {}) }], context }),

      recordExile: (id, outcome, context) =>
        get().resolveLife({ intents: [{ kind: "exile", playerId: id, outcome }], context }),

      resurrect: (id, context) =>
        get().resolveLife({ intents: [{ kind: "resurrection", playerId: id }], context }),

      spendGhostVote: (id, context) =>
        get().resolveLife({ intents: [{ kind: "spendGhostVote", playerId: id }], context }),

      restoreGhostVote: (id, context) =>
        get().resolveLife({ intents: [{ kind: "restoreGhostVote", playerId: id }], context }),

      correctLifeStatus: (id, target, context) =>
        get().resolveLife({ intents: [{ kind: "correctStatus", playerId: id, target }], context }),

      retractLifeEvent: (eventId, repair, context) =>
        get().resolveLife({ intents: [{ kind: "retractEvent", eventId }, ...repairIntents(repair)], context }),

      amendLifeEvent: (eventId, replacement, repair, context) =>
        get().resolveLife({ intents: [{ kind: "amendEvent", eventId, replacement }, ...repairIntents(repair)], context }),

      lateRecordLifeEvent: (event, repair, context) =>
        get().resolveLife({ intents: [{ kind: "lateRecord", event }, ...repairIntents(repair)], context }),

      setAlive: (id, alive, context) => {
        if (alive) get().correctLifeStatus(id, { alive: true }, context);
        else get().recordDeath(id, context);
      },

      setGhostVote: (id, ghostVote, context) => {
        if (ghostVote) get().restoreGhostVote(id, context);
        else get().spendGhostVote(id, context);
      },

      // Phase 10F (Section 6): a compatibility adapter over the authoritative
      // Life boundary -- never a second abilityUsed writer. Marking the ability
      // used is a gameplay `useAbility`; clearing it is a `correctAbilityUsed`
      // correction (a true resurrection's reset is Life's own). Like every
      // Life intent it is refused outside Night/Day. Phase 9R.4 (B8): an
      // unknown player or the already-current value is completely inert.
      setAbilityUsed: (id, abilityUsed, context) => {
        const game = get().game;
        const existing = game ? ownPlayer(game, id) : undefined;
        if (!game || !existing || existing.isEmpty) return { ok: false, code: "refused", message: "That seat has no player." };
        if (existing.abilityUsed === abilityUsed) return { ok: true, changed: false, eventIds: [] };
        return get().resolveLife({
          intents: [abilityUsed ? { kind: "useAbility", playerId: id } : { kind: "correctAbilityUsed", playerId: id, used: false }],
          ...(context ? { context } : {}),
        });
      },

      resolveEffects: (transaction) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        // guard -> plan (pure) -> one commit. The planner binds every target,
        // source and participant parameter to the participation instance the
        // caller observed, validates each intent in order against the
        // evolving working state, and returns a refusal, a true no-op, or the
        // complete effects[] + History plan.
        const result = planEffectTransaction(game, transaction);
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false, effectIds: [] };
        set({ undoStack: pushUndo(game, undoStack), game: applyEffectPlan(game, result.plan) });
        return { ok: true, changed: true, effectIds: result.plan.appliedEffectIds };
      },

      // Phase 9D.1 / 10B: the Drunk/Poisoned/Protected quick control manages
      // exactly its own deterministic manual Effect (manualEffectId(type)) --
      // never a differently-sourced Effect of the same type, so an
      // ability-created "poisoned" coexists with (and never blocks) the
      // Storyteller's manual toggle.
      setManualEffect: (target, type, on, context) => {
        const { game } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        const player = ownPlayer(game, target?.playerId);
        const effectId = manualEffectId(type);
        const existing = player?.participantId === target?.participantId
          ? player?.effects.find((e) => e.id === effectId) : undefined;
        const intent = on
          ? existing
            ? { kind: "resume" as const, target, effectId }
            : { kind: "apply" as const, target, effect: { id: effectId, type, lifetime: { kind: "manual" as const } } }
          : { kind: "remove" as const, target, effectId };
        return get().resolveEffects({ intents: [intent], ...(context ? { context } : {}) });
      },

      setStatus: (id, status, on) => {
        const target = currentBinding(get().game, id);
        if (!target) return;
        get().setManualEffect(target, status, on);
      },

      addEffect: (id, effect) => {
        const { game } = get();
        if (!game) return null;
        const target = currentBinding(game, id);
        if (!target || !effect || typeof effect !== "object") return null;
        // Phase 9R.1 (Finding B4/B5): own a deep-cloned, undefined-stripped
        // snapshot of the caller's input before anything reads it.
        const { id: effectId, sourcePlayer, appliedAt, sourceParticipant: _smuggled, ...rest } =
          cloneOwned(effect) as EffectInput & { sourceParticipant?: unknown };
        // A gameplay Apply takes hold NOW; an earlier applied moment is a
        // correction, never silently accepted here.
        if (appliedAt !== undefined && !sameSnapshot(appliedAt, effectApplicationMoment(game))) return null;
        let source: EffectParticipantBinding | undefined;
        if (sourcePlayer !== undefined) {
          source = currentBinding(game, sourcePlayer) ?? undefined;
          if (!source) return null;
        }
        const id_ = effectId ?? newId();
        const result = get().resolveEffects({ intents: [{ kind: "apply", target,
          effect: { ...rest, id: id_, ...(source ? { source } : {}) } }] });
        return result.ok ? id_ : null;
      },

      removeEffect: (id, effectId) => {
        const target = currentBinding(get().game, id);
        if (!target) return;
        get().resolveEffects({ intents: [{ kind: "remove", target, effectId }] });
      },

      resolveReminders: (transaction) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, code: "invalid", message: "No game is open." };
        // guard -> plan (pure) -> one commit. The planner binds every target
        // and source to the participation instance the caller observed,
        // validates each intent in order against the evolving working state,
        // and returns a refusal, a true no-op, or the complete reminders[] +
        // History plan.
        const result = planReminderTransaction(game, transaction);
        if (!result.ok) return result;
        if (!result.changed) return { ok: true, changed: false, reminderIds: [] };
        set({ undoStack: pushUndo(game, undoStack), game: applyReminderPlan(game, result.plan) });
        return { ok: true, changed: true, reminderIds: result.plan.placedReminderIds };
      },

      addReminder: (id, reminder) => {
        const { game } = get();
        if (!game) return null;
        const target = currentBinding(game, id);
        if (!target || !reminder || typeof reminder !== "object" || Array.isArray(reminder)) return null;
        // LUNA-10C-002: the same key-presence rule as the planner -- an
        // unsupported key (a smuggled sourceParticipant, createdAt, cleanupCue,
        // the retired lifetime...) refuses the command even when its value is
        // `undefined`; it is never stripped away. Only then own a deep-cloned,
        // undefined-stripped snapshot (Phase 9R.1 Finding B4/B5), so a KNOWN
        // optional field given as `undefined` is simply "not supplied".
        if (unknownOwnKey(reminder as unknown as Record<string, unknown>, REMINDER_INPUT_KEYS) !== undefined) return null;
        const { id: reminderId, sourcePlayer, ...rest } = cloneOwned(reminder) as ReminderInput & Record<string, unknown>;
        let source: EffectParticipantBinding | undefined;
        if (sourcePlayer !== undefined) {
          source = currentBinding(game, sourcePlayer) ?? undefined;
          if (!source) return null;
        }
        const id_ = reminderId ?? newReminderId();
        const result = get().resolveReminders({ intents: [{ kind: "place", target,
          reminder: { ...rest, id: id_, ...(source ? { source } : {}) } as never }] });
        return result.ok ? id_ : null;
      },

      removeReminder: (id, reminderId) => {
        const target = currentBinding(get().game, id);
        if (!target) return { ok: false, code: "notSeated", message: "This player is not seated." };
        return get().resolveReminders({ intents: [{ kind: "remove", target, reminderId }] });
      },

      recordInformationDelivery: (recipientPlayerId, informationActionId, values, context) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const script = selectScriptById(get(), game.scriptId);

        // Phase 10F foundation: the exact Phase 9D validation/reference
        // conversion now lives in one pure planner so a later ability
        // coordinator can compose a delivery into its evolving working
        // snapshot without creating an intermediate store commit. This
        // compatibility command still owns the same single Undo/localSeq
        // boundary it always did. An unresolvable script is reported by the
        // planner AFTER the recipient checks -- the Phase 9D refusal order.
        const result = planInformationDelivery(game, {
          recipientPlayerId,
          informationActionId,
          values,
          ...(context ? { context } : {}),
        }, {
          registry: script ? buildRegistry(script) : null,
        });
        if (!result.ok) return result;

        set({
          undoStack: pushUndo(game, undoStack),
          game: applyInformationDeliveryPlan(game, result.record),
        });
        return { ok: true, id: result.record.id };
      },

      removeInformationDelivery: (deliveryId) => {
        const { game, undoStack } = get();
        if (!game) return;
        if (!game.informationDeliveries.some((d) => d.id === deliveryId)) return;
        set({
          undoStack: pushUndo(game, undoStack),
          game: {
            ...game,
            informationDeliveries: game.informationDeliveries.filter((d) => d.id !== deliveryId),
          },
        });
      },

      setNotes: (id, notes) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        // Phase 9R.4 (B8): unknown player or already-current notes (a legacy
        // value longer than the limit, unchanged, stays a true no-op).
        const existing = ownPlayer(game, id);
        if (!existing) return { ok: false, message: "This player is not seated." };
        if (existing.stNotes === notes) return { ok: true };
        if (typeof notes !== "string" || notes.length > MAX_ST_NOTES) {
          return { ok: false, message: `Notes can be at most ${MAX_ST_NOTES} characters -- shorten them; nothing is cut off automatically.` };
        }
        set({
          undoStack: pushUndo(game, undoStack),
          game: patchPlayer(game, id, { stNotes: notes }),
        });
        return { ok: true };
      },

      setPhase: (phase) => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        if (game.phase === "ended" && phase !== "ended")
          return { ok: false, message: "This game has ended. Create a new setup to play again." };
        if (game.phase === "setup" && (phase === "night" || phase === "day")) {
          return get().beginNightOne();
        }
        // Phase 10A (10A-LUNA-RV-001): Setup is pre-game only. Once a game is
        // in Live Play (Night/Day) the phase API never moves it back to
        // Setup -- otherwise beginNightOne() would treat a running game as a
        // fresh start and re-apply starting-life canonicalization over
        // legitimate deaths, exiles and spent votes. Refused before any
        // mutation. (Undo of the initial start and adopting a genuine Setup
        // checkpoint restore whole snapshots and are unaffected.)
        if (phase === "setup" && (game.phase === "night" || game.phase === "day"))
          return { ok: false, message: "Setup is only available before live play begins." };
        // Phase 9R.4 (B8): the requested phase already holds. The ended-game
        // restriction above still applies first.
        if (game.phase === phase) return { ok: true };
        // Phase 10A (10A-ASTRA-001): live time is monotonic. Night <-> Day
        // through setPhase is exactly the next chronological phase -- the
        // same transition advancePhase() performs (Night N -> Day N, Day N ->
        // Night N+1), delegated so the progression, rollover, Undo and
        // localSeq can never drift apart. Moving to an EARLIER Game Moment
        // (e.g. Day 1 -> Night 1) would let the Life Event Window prune a
        // real event while coverage still claimed that phase was known.
        if ((game.phase === "night" || game.phase === "day") && (phase === "night" || phase === "day"))
          return get().advancePhase();
        // What remains is entering "ended" (from Setup or Live Play): the
        // window is frozen, the rollover is part of the same commit and Undo
        // step. ASTRA-10G-001: never while a lobby is attached -- an ended
        // game can never retain an active multiplayer scope (Finish Game
        // closes the session authoritatively first).
        if (get().lobby) return { ok: false, message: "Close the multiplayer lobby before ending this game (Finish game does this)." };
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, phase, lifeEventWindow: pruneLifeEventWindow(game.lifeEventWindow, phase, game.day) },
        });
        return { ok: true };
      },

      advancePhase: () => {
        const { game, undoStack } = get();
        if (!game) return { ok: false, message: "No game is open." };
        if (game.phase === "setup") return get().beginNightOne();
        // Phase 9R.4 (B8): an ended game has no next phase -- this used to
        // commit an identical game. Same refusal setPhase already gives.
        if (game.phase === "ended")
          return { ok: false, message: "This game has ended. Create a new setup to play again." };
        let { phase, day } = game;
        if (phase === "night") {
          phase = "day";
        } else if (phase === "day") {
          phase = "night";
          day = day + 1;
        }
        // Phase 10A Section 7: the Life Event Window rolls over atomically
        // with the phase change -- the same game replacement and the same
        // Undo snapshot, so undoing the advance restores every pruned event.
        // Window expiry is not a Mutation of its own: no History for it.
        //
        // Phase 10B Section 30: in that SAME replacement, every Effect whose
        // exact expiry boundary is the destination moment (or earlier) is
        // removed, with one "expire" History record each at the DESTINATION
        // moment. Never a second commit, never an intermediate state; Undo of
        // the advance restores the expired Effects with everything else.
        //
        // Phase 10G Section 6: in that SAME replacement, every Game Rule Fact
        // whose exact expiry boundary is the destination moment (or earlier)
        // expires, with one explanatory "expire" History record each -- never
        // a separate commit; Undo of the advance restores it too.
        const destination = { phase, day } as { phase: "night" | "day"; day: number };
        const expired = planEffectExpiry(game, destination);
        const expiredFacts = planGameRuleFactExpiry(game, destination);
        const advanced: StorytellerLobbyRecord = {
          ...game, phase, day, lifeEventWindow: pruneLifeEventWindow(game.lifeEventWindow, phase, day),
        };
        const withEffects = expired ? applyEffectPlan(advanced, expired) : advanced;
        set({
          undoStack: pushUndo(game, undoStack),
          game: expiredFacts ? applyGameRuleFactPlan(withEffects, expiredFacts) : withEffects,
        });
        return { ok: true };
      },

      setNightStepStatus: (day, stepKey, status) => {
        const { game, undoStack } = get();
        if (!game) return;
        // Phase 10F: the pure step plan (nightProgress.ts) -- the same rule an
        // ability resolution composes into its one final snapshot. The
        // Traveler-arrival coupling is participation-bound (v24): a step key
        // names the ParticipantId, so a replacement occupant of the seat never
        // completes (or inherits) another participant's arrival. Phase 9R.4
        // (B8): null is a true no-op (an absent key is never "already
        // current": storing it is real -- it is how a custom step exists).
        const next = planNightStepStatus(game, day, stepKey, status);
        if (!next) return;
        set({ undoStack: pushUndo(game, undoStack), game: next });
      },

      setNightStepNotes: (day, stepKey, notes) => {
        const { game } = get();
        if (!game) return { ok: false, message: "No game is open." };
        const key = `${day}:${stepKey}`;
        const np = game.nightProgress ?? {};
        const existing: NightStepRecord = np[key] ?? { status: "pending", notes: "" };
        // Phase 9R.4 (B8): already-stored notes are a true no-op (no Undo
        // here either way, but a new game still advanced localSeq). An
        // absent key is a real write -- "Add custom night step" creates its
        // step exactly this way, with empty notes.
        if (np[key]?.notes === notes) return { ok: true };
        if (typeof notes !== "string" || notes.length > MAX_NIGHT_STEP_NOTES) {
          return { ok: false, message: `Night-step notes can be at most ${MAX_NIGHT_STEP_NOTES} characters -- shorten them; nothing is cut off automatically.` };
        }
        // No undo push — avoid polluting undo stack with every keystroke.
        set({
          game: {
            ...game,
            nightProgress: { ...np, [key]: { ...existing, notes } },
          },
        });
        return { ok: true };
      },

      clearNightProgress: (day) => {
        const { game, undoStack } = get();
        if (!game) return;
        const prefix = `${day}:`;
        const next: Record<string, NightStepRecord> = {};
        let clearsSteps = false;
        for (const [k, v] of Object.entries(game.nightProgress ?? {})) {
          if (!k.startsWith(prefix)) next[k] = v;
          else clearsSteps = true;
        }
        // Phase 9R.4 (B8): nothing recorded for this night -- no step and no
        // Traveler first night completed on it -- leaves nothing to clear.
        if (!clearsSteps && !Object.values(game.players).some((p) => p.travelerArrival?.completedAtNight === day)) return;
        const players = Object.fromEntries(Object.entries(game.players).map(([id, p]) => {
          if (p.travelerArrival?.completedAtNight !== day) return [id, p];
          const arrival = { ...p.travelerArrival, firstNightComplete: false };
          delete arrival.completedAtNight;
          return [id, { ...p, travelerArrival: arrival }];
        }));
        set({
          undoStack: pushUndo(game, undoStack),
          game: { ...game, players, nightProgress: next },
        });
      },

      undo: () => {
        const { undoStack } = get();
        if (undoStack.length === 0) return;
        const previous = undoStack[undoStack.length - 1]!;
        // Undo can restore a pre-existing live snapshot after returning to Setup.
        // Validate that snapshot too; never use Undo as an unguarded first start.
        if (get().game?.phase === "setup" && (previous.phase === "night" || previous.phase === "day")) {
          const context = selectSetupContext({ ...previous, phase: "setup" }, selectScriptById(get(), previous.scriptId));
          if (!analyzeSetup(context).readiness.begin.ok) return;
        }
        set({
          game: clone(previous),
          undoStack: undoStack.slice(0, -1),
        });
      },

      setGrimoireMode: (mode) => set({ grimoireMode: mode }),

      setTokenPosition: (id, x, y) =>
        set((s) => ({
          tokenPositions: { ...s.tokenPositions, [id]: { x, y } },
        })),

      clearTokenPositions: () => set({ tokenPositions: {} }),

      ensureSyncScope: (code, sessionId) => set(state => {
        if (state.sync && state.sync.code === code && state.sync.sessionId === sessionId) return {};
        return { sync: { code, sessionId, ackedGuard: null, ackedGameSeq: 0, lastAttempt: null } };
      }),

      noteWriterAttempt: (code, sessionId, guard) => set(state => {
        if (!state.sync || state.sync.code !== code || state.sync.sessionId !== sessionId) return {};
        return { sync: { ...state.sync, lastAttempt: guard } };
      }),

      noteWriterAck: (code, sessionId, guard) => set(state => {
        if (!state.sync || state.sync.code !== code || state.sync.sessionId !== sessionId) return {};
        return {
          sync: {
            ...state.sync,
            ackedGuard: guard,
            // A successful active-writer acknowledgement reconciles the
            // matching lastAttempt; it must not clear an unrelated one (a
            // newer attempt already in flight would never equal this guard).
            lastAttempt: guardsEqual(state.sync.lastAttempt, guard) ? null : state.sync.lastAttempt,
          },
        };
      }),

      acknowledgeGameFlush: (code, sessionId, seqAtFlush) => set(state => {
        if (!state.sync || state.sync.code !== code || state.sync.sessionId !== sessionId) return {};
        if (state.sync.ackedGameSeq >= seqAtFlush) return {}; // never regress, never re-advance redundantly
        return { sync: { ...state.sync, ackedGameSeq: seqAtFlush } };
      }),

      promoteRecoveredAck: (code, sessionId, guard) => set(state => {
        if (!state.sync || state.sync.code !== code || state.sync.sessionId !== sessionId) return {};
        const attempt = state.sync.lastAttempt;
        if (!attempt || attempt.token !== guard.token || attempt.revision !== guard.revision) return {};
        const currentRevision = state.sync.ackedGuard?.revision ?? -1;
        return {
          sync: {
            ...state.sync,
            ackedGuard: guard.revision > currentRevision ? guard : state.sync.ackedGuard,
            lastAttempt: null,
          },
        };
      }),

      restoreRemoteCheckpoint: (game, guard) => set(state => ({
        game,
        undoStack: [],
        selectedPlayerId: null,
        // lastAttempt is always explicitly cleared here (Luna review,
        // Finding 1): a remote checkpoint being adopted — automatically
        // or explicitly — means whatever this local lineage was in the
        // middle of attempting is no longer relevant evidence. Preserving
        // it would let a stale attempt from BEFORE this restore later be
        // mistaken for a lost acknowledgement against a guard that is
        // actually a server rewind relative to the newly accepted
        // baseline.
        sync: state.sync && state.sync.code === game.code
          ? { ...state.sync, ackedGuard: guard, ackedGameSeq: state.localSeq, lastAttempt: null }
          : { code: game.code, sessionId: state.sync?.sessionId ?? state.lobby?.sessionId ?? "", ackedGuard: guard, ackedGameSeq: state.localSeq, lastAttempt: null },
        [SKIP_LOCAL_SEQ]: true,
      })),
      };
    },
    {
      name: "new-blood-st",
      version: STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: migrateStoreState,
      // Phase 9C.2B.2 (hardening): Zustand only invokes `migrate` above when
      // the persisted version differs from STORE_VERSION — a persisted blob
      // already tagged at the current version bypasses it (and therefore
      // migrateStoreState's own validation-or-reset tail) entirely,
      // hydrating unchanged no matter what it contains. `merge` runs on
      // EVERY rehydration regardless of version match, so routing the
      // (possibly already-migrated) persisted state through the exact same
      // canonical validator here — one path, never duplicated rules —
      // closes that gap without weakening or duplicating migration's own
      // cross-version behavior; a genuinely valid current-version save
      // still round-trips unchanged (migrateStoreState is a no-op once
      // fromVersion is no longer less than any of its own thresholds).
      merge: (persistedState, currentState) => {
        if (persistedState == null) return currentState;
        return { ...currentState, ...(migrateStoreState(persistedState, STORE_VERSION) as Partial<StorytellerStore>) };
      },
      partialize: (s) => ({
        game: s.game,
        view: s.view,
        undoStack: s.undoStack,
        customScripts: s.customScripts,
        lobby: s.lobby,
        grimoireMode: s.grimoireMode,
        tokenPositions: s.tokenPositions,
        localSeq: s.localSeq,
        sync: s.sync,
      }),
    }
  )
);

/** Own-property-safe lookup (Phase 9R.1 Astra remediation, Finding M1
 * follow-up): `BUILTIN_SCRIPTS[id] ?? s.customScripts[id]` alone resolves
 * an inherited Object.prototype member for `id` "__proto__"/"constructor"/
 * "toString" (e.g. `BUILTIN_SCRIPTS["__proto__"]` returns Object.prototype
 * itself, which is truthy) instead of correctly finding nothing. `id` is a
 * schema-validated string -- ANY string, including these -- once it has
 * passed through remote checkpoint recovery: StorytellerGamePersistedSchema
 * only requires scriptId to be a non-empty string, never that it names a
 * real script, so a scriptId of exactly "__proto__" legitimately reaches
 * this function as authoritative Current State, not merely as malformed
 * input a caller failed to sanitize. */
const ownScriptEntry = (scripts: Record<string, Script>, id: string): Script | undefined =>
  Object.prototype.hasOwnProperty.call(scripts, id) ? scripts[id] : undefined;

export const selectScriptById = (
  s: StorytellerStore,
  id: string
): Script | undefined => ownScriptEntry(BUILTIN_SCRIPTS, id) ?? ownScriptEntry(s.customScripts, id);
